export type Maybe<T> = T | null;

export interface FilterInput {
  code?: Maybe<string>;

  fullNameFilter?: Maybe<string>;

  patientProperties?: Maybe<PatientPropertiesInput>;

  toVisitDate?: Maybe<Date>;
}

export interface PatientPropertiesInput {
  date_of_birth?: Maybe<Date>;

  email?: Maybe<string>;

  encounter_text?: Maybe<string>;

  first_name?: Maybe<string>;

  last_name?: Maybe<string>;

  phone?: Maybe<string>;

  status?: Maybe<string>;

  visit_date?: Maybe<Date>;

  zip_code?: Maybe<string>;
}

export interface BuildCaseOptions {
  computeValidation?: Maybe<boolean>;

  detectSuspected?: Maybe<boolean>;

  includeEncounter?: Maybe<boolean>;

  includeImpliedFindings?: Maybe<boolean>;

  includePrompt?: Maybe<boolean>;

  includeSkip?: Maybe<boolean>;

  numericalOp?: Maybe<NumericalRefinementOperation>;

  onlySkip?: Maybe<boolean>;
}

export interface ConversationOptions {
  askAllergies?: Maybe<boolean>;

  askBackgroundDisorderSuggestions?: boolean;

  askMedication?: Maybe<boolean>;

  backgroundCategory?: boolean;

  candidatesLimitDiseasesForNextStep?: number;

  candidatesUseTopPerCuratedForNextStep?: boolean;

  clusterProbThreshold?: number;

  clustersLessLikelyMax?: number;

  clustersMostLikelyThreshold?: number;

  familyHistoryPanelId?: Maybe<string>;

  forceReasoningComputation?: Maybe<boolean>;

  heightInInches?: boolean;

  includeDiseaseCitations?: Maybe<boolean>;

  increaseNegatedSymptomsLift?: boolean;

  labQuestions?: Maybe<boolean>;

  limitBackgroundToRiskPanel?: boolean;

  maxNumOfQuestions?: number;

  medicalHistoryPanelId?: string;

  minBackgroundInformation?: number;

  minRefinementInformation?: number;

  minimumInformation?: number;

  missingPresentingSymptomsFactor?: number;

  nextStepUsingPossibleComplications?: Maybe<boolean>;

  nextSuggestionsListLimit?: number;

  notifyStatusChanges?: Maybe<boolean>;

  numericalQualification?: boolean;

  patientSortIncidenceExp?: number;

  planExpectedEntropySmoothFactor?: Maybe<number>;

  policies?: Maybe<string>;

  reduceIncidenceRate?: Maybe<boolean>;

  shouldStop?: boolean;

  sortTypeForNextStep?: SortType;

  sortTypeForResults?: SortType;

  summaryOptions?: SummaryOptions;

  temperatureInFahrenheit?: Maybe<boolean>;

  tripleQuestionCount?: number;

  tripleQuestionPercentage?: number;

  urgencySortIncidenceExp?: number;

  urgencySubTypeMin?: number;

  useDefaultPolicies?: boolean;

  usePPVForBackgroundComp?: boolean;

  usePainScaleOf10?: Maybe<boolean>;

  useTopDiseasesFromEachCluster?: number;

  volunteerBackground?: boolean;

  volunteerPanelId?: Maybe<string>;

  weightInPounds?: boolean;
}

export interface SummaryOptions {
  discardNoForNegate?: Maybe<boolean>;

  heightInInches?: boolean;

  showAssessment?: boolean;

  showIcdCodes?: Maybe<boolean>;

  weightInPounds?: boolean;
}

export interface ConversationAnswer {
  findings?: Maybe<GroupedFeature[]>;

  questionType: ConversationQuestionType;

  skipped?: Maybe<boolean>;
}

export interface GroupedFeature {
  feature?: Maybe<QualifiedFeatureInput>;

  group?: Maybe<CaseFindingPresentationType>;

  skip?: Maybe<boolean>;

  source?: Maybe<FindingSource>;
}

export interface QualifiedFeatureInput {
  alias?: Maybe<string>;

  feature?: Maybe<string>;

  negate?: Maybe<boolean>;

  qualifiedFeatureRequestId?: string;

  qualifiers?: Maybe<QualifierInput[]>;
}

export interface QualifierInput {
  encrypted?: Maybe<string>;

  freeText?: Maybe<string>;

  negate?: Maybe<boolean>;

  range?: Maybe<MeasurementRangeInput>;

  relation: string;

  source?: Maybe<FindingSource>;

  value?: Maybe<string>;
}

export interface MeasurementRangeInput {
  complexUnits?: Maybe<ComplexMeasurementUnits>;

  high?: Maybe<number>;

  low?: Maybe<number>;

  units?: Maybe<MeasurementUnits>;
}

export interface ComplexMeasurementUnits {
  denominator?: Maybe<MeasurementUnits[]>;

  numerator?: Maybe<MeasurementUnits[]>;
}

export interface ConfigParam {
  key: string;

  numValue?: Maybe<number>;

  value?: Maybe<string>;
}

export interface PatientCaseInput {
  findings?: Maybe<GroupedFeature[]>;
}

export interface CaseConditionInput {
  ddMode?: Maybe<string>;

  featureid: string;

  payload?: Maybe<CaseConditionPayloadInput>;

  sortType?: Maybe<SortType>;

  type: string;
}

export interface CaseConditionPayloadInput {
  featureid?: Maybe<string>;

  selector?: Maybe<string>;

  val?: Maybe<number>;
}

export interface PathOptions {
  adaptiveGrouping?: Maybe<boolean>;

  addPrevalenceSmoothFactor?: boolean;

  association?: boolean;

  candidatesLimitDiseases?: number;

  candidatesUseTopPerCurated?: Maybe<boolean>;

  childInherentParentEtiologies?: Maybe<boolean>;

  clusterByClosetsAncestorsInCurated?: boolean;

  clusterProbThreshold?: Maybe<number>;

  clustersLessLikelyMax?: Maybe<number>;

  clustersMostLikelyThreshold?: Maybe<number>;

  cohdPrevalences?: boolean;

  explainActiveUsingBackgroundDisorder?: Maybe<boolean>;

  extraEtiologySmoothFactor?: boolean;

  flexClustering?: Maybe<boolean>;

  flexClustering_COLLAPSE_CAP?: number;

  flexClustering_COLLAPSE_CAP_RATIO?: number;

  forceRecompute?: Maybe<boolean>;

  includeFlareUpsInDD?: boolean;

  includePathsGoingViaClosedSet?: Maybe<boolean>;

  includeRedundantInReport?: Maybe<boolean>;

  increaseNegatedSymptomsLift?: boolean;

  isa?: boolean;

  isaAsAssociation?: boolean;

  lambdaStructure?: Maybe<boolean>;

  leakByConceptType?: Maybe<boolean>;

  limitDiseasesSpace?: boolean;

  longPathsStartedInPopulation?: Maybe<boolean>;

  missingPresentingSymptomsFactor?: number;

  newKahunScore?: boolean;

  nodeEnablers?: boolean;

  noisyOrAsMax?: boolean;

  outputUsedPaths?: Maybe<boolean>;

  pathRejectTriggerLiftCap?: number;

  patientSortIncidenceExp?: number;

  population?: Maybe<boolean>;

  reduceBackgroundActiveSymptomProb?: boolean;

  reduceChronicDiseaseVsAcuteSymptomProb?: boolean;

  reduceDiamondShape?: boolean;

  reduceIncidenceRate?: boolean;

  softenActiveFindingLift?: Maybe<boolean>;

  sourceCauseTarget?: boolean;

  targetCauseSource?: boolean;

  traverseToSubTypeFinding?: boolean;

  urgencySortIncidenceExp?: number;

  urgencySubTypeMin?: number;

  useIncideneRelation?: Maybe<boolean>;

  useOnlyDiseasesToContain?: Maybe<boolean>;

  vStructure?: Maybe<boolean>;

  validLiftLimit?: Maybe<number>;
}

export interface DiseaseForPlanInput {
  disease?: Maybe<QualifiedFeatureInput>;

  prob?: Maybe<number>;
}

export interface DiseasesAliasing {
  d1?: Maybe<string>;

  d2?: Maybe<string>;
}

export interface PlanLimits {
  category?: Maybe<DiseaseCategoryType>;

  limit?: Maybe<number>;
}

export interface CohortInput {
  cohortId?: Maybe<string>;

  qf?: Maybe<QualifiedFeatureInput>;

  type: CohortInputType;
}

export interface DiseaseReferenceOptions {
  category?: Maybe<string>;
}

export interface SavedCaseInput {
  data?: Maybe<OpaqueDataInput>;

  findings?: Maybe<SavedCaseGroupInput[]>;
}

export interface OpaqueDataInput {
  format?: Maybe<string>;

  payload?: Maybe<string>;
}

export interface SavedCaseGroupInput {
  features?: Maybe<GroupedFeature[]>;

  group?: Maybe<string>;
}

export interface StoredData {
  data?: Maybe<OpaqueDataScalar>;

  features?: Maybe<GroupedFeature[]>;
}

export interface SummaryInput {
  age?: Maybe<number>;

  gender?: Maybe<string>;

  height?: Maybe<string>;

  main?: Maybe<string>;

  sections?: Maybe<SummerySectionInput[]>;

  visitReason?: Maybe<SummaryVisitReasonInput[]>;

  weight?: Maybe<string>;
}

export interface SummerySectionInput {
  content?: Maybe<string>;

  title?: Maybe<string>;

  type?: Maybe<SummarySectionType>;
}

export interface SummaryVisitReasonInput {
  id?: Maybe<string>;

  name?: Maybe<string>;
}

export enum SavedCaseStatus {
  Fail = "FAIL",
  Mismatch = "MISMATCH",
  Success = "SUCCESS",
}

export enum Order {
  Asc = "Asc",
  Desc = "Desc",
}

export enum MeasurementUnits {
  BeatsPerMinute = "BeatsPerMinute",
  Celsius = "Celsius",
  Centimeters = "Centimeters",
  Days = "Days",
  Deciliter = "Deciliter",
  Fahrenheit = "Fahrenheit",
  Feet = "Feet",
  Femtoliter = "Femtoliter",
  GigaUnits = "GigaUnits",
  GramCreatinine = "GramCreatinine",
  GramHemoglobin = "GramHemoglobin",
  Grams = "Grams",
  Hours = "Hours",
  Inches = "Inches",
  KgBodyWeight = "KgBodyWeight",
  KiloUnits = "KiloUnits",
  KilogramH2O = "KilogramH2O",
  Kilograms = "Kilograms",
  Liter = "Liter",
  MegaUnits = "MegaUnits",
  MeterSquare = "MeterSquare",
  Meters = "Meters",
  MicroUnits = "MicroUnits",
  Micrograms = "Micrograms",
  Microliter = "Microliter",
  MilliMole = "MilliMole",
  MilliOsmolality = "MilliOsmolality",
  MilliUnits = "MilliUnits",
  Milliequivalent = "Milliequivalent",
  Milligrams = "Milligrams",
  Milliliter = "Milliliter",
  Millimeters = "Millimeters",
  MmHg = "MmHg",
  Mole = "Mole",
  Months = "Months",
  Na = "NA",
  NanoMole = "NanoMole",
  Nanograms = "Nanograms",
  Pack = "Pack",
  Percent = "Percent",
  PercentOfNormal = "PercentOfNormal",
  PicoMole = "PicoMole",
  Picograms = "Picograms",
  Pounds = "Pounds",
  Priority = "Priority",
  Seconds = "Seconds",
  Times = "Times",
  Units = "Units",
  Weeks = "Weeks",
  Years = "Years",
}

export enum FindingSource {
  Criteria = "CRITERIA",
  Ehr = "EHR",
  Implied = "IMPLIED",
  Patient = "PATIENT",
  PatientFreeText = "PATIENT_FREE_TEXT",
  PatientVolunteer = "PATIENT_VOLUNTEER",
  Physician = "PHYSICIAN",
  Possible = "POSSIBLE",
}

export enum SubGroupType {
  DueTo = "DUE_TO",
  LimitedTo = "LIMITED_TO",
  Subtype = "SUBTYPE",
}

export enum UserRoles {
  KahunBackofficeUsers = "Kahun_Backoffice_Users",
  KahunClinicAdmins = "Kahun_Clinic_Admins",
  KahunClinicManager = "Kahun_Clinic_Manager",
  KahunClinicSuperAdmins = "Kahun_Clinic_Super_Admins",
  KahunClinicUser = "Kahun_Clinic_User",
  KahunPatientDemoUsers = "Kahun_PatientDemo_Users",
}

export enum NumericalRefinementOperation {
  Merge = "MERGE",
  None = "NONE",
  ReplaceWithDetailed = "REPLACE_WITH_DETAILED",
}

export enum SortType {
  BestMatch = "BestMatch",
  PatientProb = "PatientProb",
  Prob = "Prob",
  Urgency = "Urgency",
}

export enum CaseFindingPresentationType {
  ActiveFinding = "ACTIVE_FINDING",
  BackgroundFinding = "BACKGROUND_FINDING",
  MainFinding = "MAIN_FINDING",
}

export enum ValidationStatus {
  Invalidated = "invalidated",
  None = "none",
  RequiresValidation = "requires_validation",
  Validated = "validated",
  ValidationFollowup = "validation_followup",
}

export enum SubStatus {
  Incomplete = "INCOMPLETE",
  Skipped = "SKIPPED",
}

export enum ConversationQuestionType {
  Age = "AGE",
  Allergies = "ALLERGIES",
  BackgroundSuggestion = "BACKGROUND_SUGGESTION",
  ChiefComplaint = "CHIEF_COMPLAINT",
  Date = "DATE",
  Duration = "DURATION",
  Final = "FINAL",
  Form = "FORM",
  FreeText = "FREE_TEXT",
  FreeTextShort = "FREE_TEXT_SHORT",
  Gender = "GENDER",
  InsufficientData = "INSUFFICIENT_DATA",
  Labs = "LABS",
  Medications = "MEDICATIONS",
  MultipleChiefComplaint = "MULTIPLE_CHIEF_COMPLAINT",
  OtherComplaint = "OTHER_COMPLAINT",
  Pregnancy = "PREGNANCY",
  ReasonForVisit = "REASON_FOR_VISIT",
  Suggestion = "SUGGESTION",
  VolunteerBackgroud = "VOLUNTEER_BACKGROUD",
  VolunteerFamilyHistory = "VOLUNTEER_FAMILY_HISTORY",
}

export enum AdjustmentReason {
  AcuteToChronic = "AcuteToChronic",
  BackgroundToActive = "BackgroundToActive",
  ChronicToAcute = "ChronicToAcute",
  DividByZero = "DividByZero",
  EqualNegativeSubtype = "EqualNegativeSubtype",
  NegativeSymptom = "NegativeSymptom",
  RedivideSubtypesLift = "RedivideSubtypesLift",
  ReduceIncidence = "ReduceIncidence",
  SoftenActiveFindings = "SoftenActiveFindings",
}

export enum CrCausality {
  Association = "ASSOCIATION",
  DiseaseCausesFinding = "DISEASE_CAUSES_FINDING",
  DiseaseIsaFinding = "DISEASE_ISA_FINDING",
  FindingCausesDisease = "FINDING_CAUSES_DISEASE",
  FindingIsaDisease = "FINDING_ISA_DISEASE",
  Uncorrelated = "UNCORRELATED",
}

export enum EtiologySection {
  Causality = "CAUSALITY",
  Etiology = "ETIOLOGY",
  Risk = "RISK",
}

export enum DdFlagType {
  Atypical = "ATYPICAL",
  BecauseOf = "BECAUSE_OF",
  Common = "COMMON",
  ExplainedBy = "EXPLAINED_BY",
  FlareUp = "FLARE_UP",
  GoodMatch = "GOOD_MATCH",
  HighPredictiveValue = "HIGH_PREDICTIVE_VALUE",
  NoCorrolation = "NO_CORROLATION",
  Rare = "RARE",
  Urgent = "URGENT",
}

export enum FoundInPatient {
  Has = "HAS",
  Hasnot = "HASNOT",
  HasnotSupertype = "HASNOT_SUPERTYPE",
  HasSubtype = "HAS_SUBTYPE",
  Unknown = "UNKNOWN",
}

export enum MatchedRankingType {
  ChildOfTargetCondition = "CHILD_OF_TARGET_CONDITION",
  Exact = "EXACT",
  ParentOfTargetCondition = "PARENT_OF_TARGET_CONDITION",
}

export enum DiseaseCategoryType {
  Association = "ASSOCIATION",
  Background = "BACKGROUND",
  Etiology = "ETIOLOGY",
  Imaging = "IMAGING",
  NoCorrelation = "NO_CORRELATION",
  Other = "OTHER",
  OutcomeComplication = "OUTCOME_COMPLICATION",
  OutcomeSigns = "OUTCOME_SIGNS",
  OutcomeSignsOnly = "OUTCOME_SIGNS_ONLY",
  OutcomeSymptomOnly = "OUTCOME_SYMPTOM_ONLY",
  OutcomeTest = "OUTCOME_TEST",
  OutcomeVitalsign = "OUTCOME_VITALSIGN",
  Overview = "OVERVIEW",
  OverviewTopPpv = "OVERVIEW_TOP_PPV",
  OverviewTopSensitivity = "OVERVIEW_TOP_SENSITIVITY",
  PatientFinding = "PATIENT_FINDING",
  PatientFindingsDecendants = "PATIENT_FINDINGS_DECENDANTS",
  Population = "POPULATION",
  Risks = "RISKS",
}

export enum Exactness {
  Computed = "Computed",
  Exact = "Exact",
  ExpertEstimation = "ExpertEstimation",
}

export enum QuestionChoiceType {
  ChooseMuliple = "ChooseMuliple",
  ChooseNumeric = "ChooseNumeric",
  ChooseOne = "ChooseOne",
}

export enum ConnectionStrength {
  Medium = "MEDIUM",
  None = "NONE",
  Strong = "STRONG",
  Weak = "WEAK",
}

export enum RuleType {
  Association = "ASSOCIATION",
  Companion = "COMPANION",
  Isa = "ISA",
  Lift = "LIFT",
  SourceCauseTarget = "SOURCE_CAUSE_TARGET",
  Subtype = "SUBTYPE",
  TargetCauseSource = "TARGET_CAUSE_SOURCE",
}

export enum NodeMatch {
  Exact = "EXACT",
  None = "NONE",
  Sibling = "SIBLING",
  Subtype = "SUBTYPE",
  Supertype = "SUPERTYPE",
}

export enum StatementHypothesis {
  Association = "ASSOCIATION",
  Causes = "CAUSES",
  DoesNotCause = "DOES_NOT_CAUSE",
  RiskFactor = "RISK_FACTOR",
}

export enum MetricsRuleUnits {
  HazardRatio = "HAZARD_RATIO",
  Incidence = "INCIDENCE",
  LifetimePerrcent = "LIFETIME_PERRCENT",
  Lift = "LIFT",
  Nlr = "NLR",
  Npv = "NPV",
  OddsRatio = "ODDS_RATIO",
  Percent = "PERCENT",
  Plr = "PLR",
  Ppv = "PPV",
  RelativeRisk = "RELATIVE_RISK",
  Specificity = "SPECIFICITY",
}

export enum NodeCausality {
  DiseaseCausesNode = "DISEASE_CAUSES_NODE",
  Mixed = "MIXED",
  NodeCausesDisease = "NODE_CAUSES_DISEASE",
  Uncorrelated = "UNCORRELATED",
}

export enum NodeType {
  Disease = "DISEASE",
  Finding = "FINDING",
  FindingSubtype = "FINDING_SUBTYPE",
  Inner = "INNER",
}

export enum SummarySectionType {
  Assessment = "assessment",
  Dd = "dd",
  FreeForm = "free_form",
  Hpi = "hpi",
  Imaging = "imaging",
  Labs = "labs",
  Other = "other",
  Pe = "pe",
  Plan = "plan",
  ReasonForVisit = "reason_for_visit",
  Referral = "referral",
  Ros = "ros",
  Suggested = "suggested",
  TriageAdvice = "triage_advice",
}

export enum MissingInCaseAction {
  AnswerNo = "ANSWER_NO",
  Skip = "SKIP",
}

export enum ConversationPatientAnswer {
  No = "no",
  Unknown = "unknown",
  Yes = "yes",
}

export enum ConversationStage {
  ChiefComplaintComplete = "CHIEF_COMPLAINT_COMPLETE",
  DemographicComplete = "DEMOGRAPHIC_COMPLETE",
  Start = "START",
}

export enum SearchFilter {
  All = "ALL",
  Conditions = "CONDITIONS",
  Demographics = "DEMOGRAPHICS",
  History = "HISTORY",
  Symptoms = "SYMPTOMS",
  Tests = "TESTS",
}

export enum PatientSummaryMode {
  Patient = "PATIENT",
  Provider = "PROVIDER",
}

export enum CodeSet {
  Kahun = "kahun",
  Loinc = "loinc",
  Snomed = "snomed",
  Synonym = "synonym",
}

export enum CohortInputType {
  CohortId = "CohortId",
  QfInput = "QFInput",
}

export enum CitationType {
  Direct = "direct",
  FromSource = "fromSource",
  Intermediate = "intermediate",
}

export enum DiseaseFindingDirection {
  Association = "association",
  Following = "following",
  Preceeding = "preceeding",
}

export enum GVersionState {
  Candidate = "CANDIDATE",
  Failed = "FAILED",
  Invalid = "INVALID",
  Loading = "LOADING",
  New = "NEW",
  Obsolete = "OBSOLETE",
  Ready = "READY",
}

export enum FindingGroupType {
  ClosedSet = "CLOSED_SET",
  Incidence = "INCIDENCE",
  Leaf = "LEAF",
  Procedure = "PROCEDURE",
  Qualifiers = "QUALIFIERS",
  Subtypes = "SUBTYPES",
}

export enum Permissions {
  AllClinicAccess = "ALL_CLINIC_ACCESS",
  ClinicBackoffice = "CLINIC_BACKOFFICE",
  ClinicCustomize = "CLINIC_CUSTOMIZE",
  ClinicDelete = "CLINIC_DELETE",
  ClinicEditPatientsData = "CLINIC_EDIT_PATIENTS_DATA",
  ClinicGetPatientsData = "CLINIC_GET_PATIENTS_DATA",
  ClinicManageUsers = "CLINIC_MANAGE_USERS",
  CodeDelete = "CODE_DELETE",
  Condition = "CONDITION",
  CopyCasesBetweenClinics = "COPY_CASES_BETWEEN_CLINICS",
  Graph = "GRAPH",
  Model = "MODEL",
  Mutations = "MUTATIONS",
  Reasoning = "REASONING",
}

export enum Test {
  X = "x",
  Y = "y",
}

export type UpdateSummaryErrors = any;

/** The `JSONObject` scalar type represents JSON objects as specified by [ECMA-404](http://www.ecma-international.org/publications/files/ECMA-ST/ECMA-404.pdf). */
export type JsonObject = any;

export type Date = any;

export type StartKey = any;

export type PatientProperties = any;

export type VisitStatus = any;

/** The `JSONObject` scalar type represents JSON objects as specified by [ECMA-404](http://www.ecma-international.org/publications/files/ECMA-ST/ECMA-404.pdf). */
export type ConversationContext = any;

/** shouldStop: boolean promptMinimumInformation: number minimumInformation: number minRefinementInformation: number minBackgroundInformation: number tripleQuestionCount: number summaryOptions.discardNoForNegate: boolean summaryOptions.heightInInches: boolean summaryOptions.showAssessment: boolean summaryOptions.weightInPounds: boolean summaryOptions.showIcdCodes: boolean numericalQualification: boolean usePainScaleOf10: boolean policies: string excludedProtocols: string useDefaultPolicies: boolean temperatureInFahrenheit: boolean includeDiseaseCitations: boolean planExpectedEntropySmoothFactor: number nextSuggestionsListLimit: number useTopDiseasesFromEachCluster: number limitDiseasesForNextStep: number maxNumOfQuestions: number forceReasoningComputation: boolean notifyStatusChanges: boolean sortTypeForNextStep: string sortTypeForResults: string medicalHistoryPanelId: string familyHistoryPanelId: string askMedication: boolean askAllergies: boolean askFreeText: boolean endChatOnRecommendation: string customPatientFacingPropertyValues: string nextStepDD.addPrevalenceSmoothFactor: boolean nextStepDD.traverseToSubTypeFinding: boolean nextStepDD.explainActiveUsingBackgroundDisorder: boolean nextStepDD.softenActiveFindingLift: boolean nextStepDD.increaseNegatedSymptomsLift: boolean nextStepDD.noisyOrAsMax: boolean nextStepDD.forceRecompute: boolean nextStepDD.outputUsedPaths: boolean nextStepDD.allCandidatePathsAsUsedPaths: boolean nextStepDD.includeFlareUpsInDD: boolean nextStepDD.reduceChronicDiseaseVsAcuteSymptomProb: boolean nextStepDD.reduceBackgroundActiveSymptomProb: boolean nextStepDD.useOnlyDiseasesToContain: boolean nextStepDD.flexClustering: boolean nextStepDD.flexClustering_COLLAPSE_CAP: number nextStepDD.flexClustering_COLLAPSE_CAP_RATIO: number nextStepDD.urgencySortIncidenceExp: number nextStepDD.patientSortIncidenceExp: number nextStepDD.candidatesUseTopPerCurated: boolean nextStepDD.candidatesLimitDiseases: number nextStepDD.validLiftLimit: number nextStepDD.totalLiftLimit: number nextStepDD.abnormalLiftLimit: number nextStepDD.mainFindingLeakFactor: number nextStepDD.volunteeredSourceLeakFactor: number nextStepDD.suggestedSourceLeakFactor: number nextStepDD.physicianSourceLeakFactor: number nextStepDD.clustersMostLikelyThreshold: number nextStepDD.clustersLessLikelyMax: number nextStepDD.clusterProbThreshold: number nextStepDD.urgencySubTypeMin: number nextStepDD.missingPresentingSymptomsFactor: number nextStepDD.includeRedundantInReport: boolean nextStepDD.topBottom2ndPass: boolean nextStepDD.filterCovid: boolean nextStepDD.possibleComplications: boolean nextStepDD.sortTypeOfPossibleComplications: string nextStepDD.outputSteps: boolean nextStepDD.localizedDiseases: boolean nextStepDD.outputFullSubtypesHierarchyExplenations: boolean nextStepDD.policies: string nextStepDD.redundancyFilters: string nextStepDD.useLLM: boolean nextStepDD.llmOptions.model: string nextStepDD.llmOptions.ddPrompt: string nextStepDD.llmOptions.explanationPrompt: string nextStepDD.llmOptions.workupPrompt: string nextStepDD.llmOptions.assessmentPrompt: string nextStepDD.llmOptions.medicalNotePrompt: string nextStepDD.llmOptions.ddMaxTokens: number nextStepDD.llmOptions.explanationtMaxTokens: number nextStepDD.llmOptions.askForExplanation: boolean nextStepDD.llmOptions.useKahunSummaryAsInput: boolean nextStepDD.llmOptions.useLLMGeneratedSummaryOutput: boolean nextStepDD.llmOptions.groundAssessment: boolean nextStepDD.llmOptions.groundExplanation: boolean protocolsQuestionGroupCount: number groupQuestionsByRule: boolean resultsDD.addPrevalenceSmoothFactor: boolean resultsDD.traverseToSubTypeFinding: boolean resultsDD.explainActiveUsingBackgroundDisorder: boolean resultsDD.softenActiveFindingLift: boolean resultsDD.increaseNegatedSymptomsLift: boolean resultsDD.noisyOrAsMax: boolean resultsDD.forceRecompute: boolean resultsDD.outputUsedPaths: boolean resultsDD.allCandidatePathsAsUsedPaths: boolean resultsDD.includeFlareUpsInDD: boolean resultsDD.reduceChronicDiseaseVsAcuteSymptomProb: boolean resultsDD.reduceBackgroundActiveSymptomProb: boolean resultsDD.useOnlyDiseasesToContain: boolean resultsDD.flexClustering: boolean resultsDD.flexClustering_COLLAPSE_CAP: number resultsDD.flexClustering_COLLAPSE_CAP_RATIO: number resultsDD.urgencySortIncidenceExp: number resultsDD.patientSortIncidenceExp: number resultsDD.candidatesUseTopPerCurated: boolean resultsDD.candidatesLimitDiseases: number resultsDD.validLiftLimit: number resultsDD.totalLiftLimit: number resultsDD.abnormalLiftLimit: number resultsDD.mainFindingLeakFactor: number resultsDD.volunteeredSourceLeakFactor: number resultsDD.suggestedSourceLeakFactor: number resultsDD.physicianSourceLeakFactor: number resultsDD.clustersMostLikelyThreshold: number resultsDD.clustersLessLikelyMax: number resultsDD.clusterProbThreshold: number resultsDD.urgencySubTypeMin: number resultsDD.missingPresentingSymptomsFactor: number resultsDD.includeRedundantInReport: boolean resultsDD.topBottom2ndPass: boolean resultsDD.filterCovid: boolean resultsDD.possibleComplications: boolean resultsDD.sortTypeOfPossibleComplications: string resultsDD.outputSteps: boolean resultsDD.localizedDiseases: boolean resultsDD.outputFullSubtypesHierarchyExplenations: boolean resultsDD.policies: string resultsDD.redundancyFilters: string resultsDD.useLLM: boolean resultsDD.llmOptions.model: string resultsDD.llmOptions.ddPrompt: string resultsDD.llmOptions.explanationPrompt: string resultsDD.llmOptions.workupPrompt: string resultsDD.llmOptions.assessmentPrompt: string resultsDD.llmOptions.medicalNotePrompt: string resultsDD.llmOptions.ddMaxTokens: number resultsDD.llmOptions.explanationtMaxTokens: number resultsDD.llmOptions.askForExplanation: boolean resultsDD.llmOptions.useKahunSummaryAsInput: boolean resultsDD.llmOptions.useLLMGeneratedSummaryOutput: boolean resultsDD.llmOptions.groundAssessment: boolean resultsDD.llmOptions.groundExplanation: boolean localizedSuggestions: boolean skipIncompleteInformationQuestion: boolean numericDuration: boolean reasonForVisitPanel: string sendPregnancyFollowUpForm: boolean enableCriteria: boolean useMIforUrgencyDifferentiation: boolean enableProtocolsInTestingStage: boolean multipleComplaints: boolean */
export type ConversationOptionsInput = any;

export type StructuredDebugInfo = any;

/** addPrevalenceSmoothFactor: boolean traverseToSubTypeFinding: boolean explainActiveUsingBackgroundDisorder: boolean softenActiveFindingLift: boolean increaseNegatedSymptomsLift: boolean noisyOrAsMax: boolean forceRecompute: boolean outputUsedPaths: boolean allCandidatePathsAsUsedPaths: boolean includeFlareUpsInDD: boolean reduceChronicDiseaseVsAcuteSymptomProb: boolean reduceBackgroundActiveSymptomProb: boolean useOnlyDiseasesToContain: boolean flexClustering: boolean flexClustering_COLLAPSE_CAP: number flexClustering_COLLAPSE_CAP_RATIO: number urgencySortIncidenceExp: number patientSortIncidenceExp: number candidatesUseTopPerCurated: boolean candidatesLimitDiseases: number validLiftLimit: number totalLiftLimit: number abnormalLiftLimit: number mainFindingLeakFactor: number volunteeredSourceLeakFactor: number suggestedSourceLeakFactor: number physicianSourceLeakFactor: number clustersMostLikelyThreshold: number clustersLessLikelyMax: number clusterProbThreshold: number urgencySubTypeMin: number missingPresentingSymptomsFactor: number includeRedundantInReport: boolean topBottom2ndPass: boolean filterCovid: boolean possibleComplications: boolean sortTypeOfPossibleComplications: string outputSteps: boolean localizedDiseases: boolean outputFullSubtypesHierarchyExplenations: boolean policies: string redundancyFilters: string useLLM: boolean llmOptions.model: string llmOptions.ddPrompt: string llmOptions.explanationPrompt: string llmOptions.workupPrompt: string llmOptions.assessmentPrompt: string llmOptions.medicalNotePrompt: string llmOptions.ddMaxTokens: number llmOptions.explanationtMaxTokens: number llmOptions.askForExplanation: boolean llmOptions.useKahunSummaryAsInput: boolean llmOptions.useLLMGeneratedSummaryOutput: boolean llmOptions.groundAssessment: boolean llmOptions.groundExplanation: boolean */
export type DiagnosisOptionsInput = any;

export type RuleRef = any;

export type Statement = any;

export type SummaryOptionsInput = any;

export type OpaqueDataScalar = any;

// ====================================================
// Documents
// ====================================================

export type GetUserRolesVariables = {};

export type GetUserRolesQuery = {
  __typename?: "Query";

  getUserRoles: Maybe<string[]>;
};

export type DdOverviewVariables = {
  qfs?: Maybe<QualifiedFeatureInput[]>;
  consider?: Maybe<QualifiedFeatureInput[]>;
  pathOptions?: Maybe<PathOptions>;
  diagnosisOptions?: Maybe<DiagnosisOptionsInput>;
  depth?: Maybe<number>;
  limit?: Maybe<number>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type DdOverviewQuery = {
  __typename?: "Query";

  dd: Maybe<DdOverviewDd>;
};

export type DdOverviewDd = {
  __typename?: "DDResponse";

  citations: Maybe<string[]>;

  clinicalReasoning: Maybe<DdOverviewClinicalReasoning[]>;
};

export type DdOverviewClinicalReasoning = {
  __typename?: "ClinicalReasoning";

  id: string;

  disease: Maybe<DdOverviewDisease[]>;

  inInitialDisorders: Maybe<boolean>;

  isKahunDisorder: Maybe<boolean>;

  forcedInBestMatch: Maybe<boolean>;

  forcedInHighestProb: Maybe<boolean>;

  forcedInSort: Maybe<SortType[]>;

  score: Maybe<number>;

  urgency: Maybe<number>;

  diseaseGivenIndependentFindings: Maybe<number>;

  diseaseGivenDependentFindings: Maybe<number>;

  liftWithDependentFindings: Maybe<number>;

  prevalence: Maybe<number>;

  prevalenceDescription: Maybe<string>;

  incidence: Maybe<number>;

  incidenceDescription: Maybe<string>;

  isRedundant: Maybe<boolean>;

  flags: Maybe<DdOverviewFlags[]>;
};

export type DdOverviewDisease = QualifiedFeatureFragment;

export type DdOverviewFlags = FlagFragment;

export type DdWithCaseVariables = {
  qfs?: Maybe<GroupedFeature[]>;
  considerPanels?: Maybe<string[]>;
  diseasesToDiscard?: Maybe<QualifiedFeatureInput[]>;
  diseasesToAdd?: Maybe<QualifiedFeatureInput[]>;
  nextStepDiseasesToConsider?: Maybe<QualifiedFeatureInput[]>;
  pathOptions?: Maybe<PathOptions>;
  diagnosisOptions?: Maybe<DiagnosisOptionsInput>;
  depth?: Maybe<number>;
  limit?: Maybe<number>;
  withDebug: boolean;
  adaptiveRatio?: Maybe<number>;
  qfLight?: Maybe<boolean>;
  sortTypes?: Maybe<SortType[]>;
  conditions?: Maybe<CaseConditionInput[]>;
  backEndMode?: Maybe<boolean>;
  includeCitations?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  caseId?: Maybe<string>;
};

export type DdWithCaseQuery = {
  __typename?: "Query";

  dd: Maybe<DdWithCaseDd>;
};

export type DdWithCaseDd = {
  __typename?: "DDResponse";

  allFindings: Maybe<DdWithCaseAllFindings[]>;

  citations: Maybe<string[]>;

  clinicalReasoning: Maybe<DdWithCaseClinicalReasoning[]>;

  meetsConditions: Maybe<DdWithCaseMeetsConditions[]>;

  strength: Maybe<number>;

  strengthForSortType: Maybe<DdWithCaseStrengthForSortType[]>;

  clustersToConsider: Maybe<DdWithCaseClustersToConsider[]>;

  debugSheet: Maybe<DdWithCaseDebugSheet>;

  debugReport: Maybe<string[]>;

  debugSheets: Maybe<DdWithCaseDebugSheets[]>;

  urgencyBySortType: Maybe<DdWithCaseUrgencyBySortType[]>;

  options: Maybe<DiagnosisOptionsInput>;
};

export type DdWithCaseAllFindings = QualifiedFeatureFragment;

export type DdWithCaseClinicalReasoning = {
  __typename?: "ClinicalReasoning";

  id: string;

  disease: Maybe<DdWithCaseDisease[]>;

  diseaseCitations: Maybe<DdWithCaseDiseaseCitations>;

  scores: Maybe<DdWithCaseScores[]>;

  findings: Maybe<DdWithCaseFindings[]>;

  subTypes: Maybe<DdWithCaseSubTypes[]>;

  inInitialDisorders: Maybe<boolean>;

  isKahunDisorder: Maybe<boolean>;

  forcedInBestMatch: Maybe<boolean>;

  forcedInHighestProb: Maybe<boolean>;

  forcedInSort: Maybe<SortType[]>;

  missingPresentingSymptoms: Maybe<boolean>;

  presentableDisease: Maybe<boolean>;

  score: Maybe<number>;

  urgency: Maybe<number>;

  diseaseGivenIndependentFindings: Maybe<number>;

  diseaseGivenDependentFindings: Maybe<number>;

  liftWithDependentFindings: Maybe<number>;

  prevalence: Maybe<number>;

  prevalenceDescription: Maybe<string>;

  incidence: Maybe<number>;

  incidenceDescription: Maybe<string>;

  isRedundant: Maybe<boolean>;

  inTopCR: Maybe<boolean>;

  inTopCRPerSortType: Maybe<SortType[]>;

  patientIncidence: Maybe<number>;

  flags: Maybe<DdWithCaseFlags[]>;

  redundantDueToBySort: Maybe<DdWithCaseRedundantDueToBySort[]>;

  matchedRankings: Maybe<DdWithCaseMatchedRankings[]>;
};

export type DdWithCaseDisease = {
  __typename?: "QualifiedFeature";

  urgencyInfo: Maybe<DdWithCaseUrgencyInfo>;
} & QualifiedFeatureFragment;

export type DdWithCaseUrgencyInfo = {
  __typename?: "Feature";

  id: string;

  urgency: Maybe<number>;

  urgencyId: Maybe<string>;
};

export type DdWithCaseDiseaseCitations = CitationsFragment;

export type DdWithCaseScores = {
  __typename?: "ScoreByType";

  sortType: Maybe<SortType>;

  adjusted: Maybe<number>;

  postClusterAdjustment: Maybe<number>;
};

export type DdWithCaseFindings = {
  __typename?: "ClinicalReasoningFinding";

  finding: Maybe<DdWithCaseFinding>;

  sourceQFIndex: Maybe<number>;

  absolutePredictiveValue: Maybe<number>;

  likelihoodRatio: Maybe<number>;
};

export type DdWithCaseFinding = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type DdWithCaseSubTypes = QualifiedFeatureFragment;

export type DdWithCaseFlags = FlagFragment;

export type DdWithCaseRedundantDueToBySort = {
  __typename?: "RedundantBySort";

  sortType: Maybe<SortType>;

  redundantDueTo: Maybe<DdWithCaseRedundantDueTo>;
};

export type DdWithCaseRedundantDueTo = QualifiedFeatureFragment;

export type DdWithCaseMatchedRankings = {
  __typename?: "MatchedRanking";

  matchType: Maybe<MatchedRankingType>;

  condition: Maybe<DdWithCaseCondition>;
};

export type DdWithCaseCondition = {
  __typename?: "CaseCondition";

  sortType: Maybe<SortType>;

  ddMode: Maybe<string>;

  feature: Maybe<DdWithCaseFeature>;
};

export type DdWithCaseFeature = {
  __typename?: "Feature";

  id: string;
};

export type DdWithCaseMeetsConditions = {
  __typename?: "MeetCondition";

  condition: Maybe<DdWithCase_Condition>;

  meets: Maybe<boolean>;
};

export type DdWithCase_Condition = {
  __typename?: "CaseCondition";

  feature: Maybe<DdWithCase_Feature>;

  type: Maybe<string>;

  sortType: Maybe<SortType>;

  ddMode: Maybe<string>;

  payload: Maybe<DdWithCasePayload>;
};

export type DdWithCase_Feature = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type DdWithCasePayload = {
  __typename?: "CaseConditionPayload";

  feature: Maybe<DdWithCase__Feature>;

  val: Maybe<number>;
};

export type DdWithCase__Feature = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type DdWithCaseStrengthForSortType = {
  __typename?: "StrengthForSortType";

  sortType: Maybe<SortType>;

  strength: Maybe<number>;
};

export type DdWithCaseClustersToConsider = {
  __typename?: "DiseaseCluster";

  id: string;

  crIds: Maybe<string[]>;

  sortType: Maybe<SortType>;

  title: Maybe<DdWithCaseTitle>;

  bestCrId: Maybe<string>;

  flags: Maybe<DdFlagType[]>;

  citations: Maybe<DdWithCaseCitations>;

  urgencyId: Maybe<string>;

  scores: Maybe<DdWithCase_Scores>;
};

export type DdWithCaseTitle = {
  __typename?: "QualifiedFeature";

  urgencyInfo: Maybe<DdWithCase_UrgencyInfo>;
} & QualifiedFeatureFragment;

export type DdWithCase_UrgencyInfo = {
  __typename?: "Feature";

  id: string;

  urgency: Maybe<number>;

  urgencyId: Maybe<string>;
};

export type DdWithCaseCitations = CitationsFragment;

export type DdWithCase_Scores = {
  __typename?: "ScoreByType";

  adjustedNormalized: Maybe<number>;
};

export type DdWithCaseDebugSheet = {
  __typename?: "Sheet";

  rows: Maybe<DdWithCaseRows[]>;
};

export type DdWithCaseRows = {
  __typename?: "Row";

  cols: Maybe<string[]>;
};

export type DdWithCaseDebugSheets = DebugSheetFragment;

export type DdWithCaseUrgencyBySortType = {
  __typename?: "UrgencyByType";

  sortType: Maybe<SortType>;

  urgencyLevelId: Maybe<string>;
};

export type DdReportOnlyVariables = {
  qfs?: Maybe<GroupedFeature[]>;
  considerPanels?: Maybe<string[]>;
  diseasesToDiscard?: Maybe<QualifiedFeatureInput[]>;
  diseasesToAdd?: Maybe<QualifiedFeatureInput[]>;
  nextStepDiseasesToConsider?: Maybe<QualifiedFeatureInput[]>;
  pathOptions?: Maybe<PathOptions>;
  diagnosisOptions?: Maybe<DiagnosisOptionsInput>;
  depth?: Maybe<number>;
  limit?: Maybe<number>;
  adaptiveRatio?: Maybe<number>;
  sortTypes?: Maybe<SortType[]>;
  conditions?: Maybe<CaseConditionInput[]>;
};

export type DdReportOnlyQuery = {
  __typename?: "Query";

  dd: Maybe<DdReportOnlyDd>;
};

export type DdReportOnlyDd = {
  __typename?: "DDResponse";

  debugReport: Maybe<string[]>;
};

export type PlanVariables = {
  diseases?: Maybe<DiseaseForPlanInput[]>;
  patientCase?: Maybe<PatientCaseInput>;
  pathOptions?: Maybe<PathOptions>;
  diagnosisOptions?: Maybe<DiagnosisOptionsInput>;
  withDebug: boolean;
  qfLight?: Maybe<boolean>;
  limit?: Maybe<number>;
  categories?: Maybe<DiseaseCategoryType[]>;
  withDiseaseMetrics?: Maybe<boolean>;
  limitPerCategory?: Maybe<PlanLimits[]>;
  diseasesAliasing?: Maybe<DiseasesAliasing[]>;
  includeFeatureRange?: Maybe<boolean>;
  caseId?: Maybe<string>;
};

export type PlanQuery = {
  __typename?: "Query";

  plan: Maybe<PlanPlan>;
};

export type PlanPlan = PatientSuggestionsFragment;

export type RestoreCaseVariables = {
  findings?: Maybe<QualifiedFeatureInput[]>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type RestoreCaseQuery = {
  __typename?: "Query";

  qualifiedFeatures: Maybe<RestoreCaseQualifiedFeatures[]>;
};

export type RestoreCaseQualifiedFeatures = QualifiedFeatureFragment;

export type PatientCaseMetaDataVariables = {
  id?: Maybe<string>;
};

export type PatientCaseMetaDataQuery = {
  __typename?: "Query";

  patientCaseMetaData: Maybe<PatientCaseMetaDataPatientCaseMetaData>;
};

export type PatientCaseMetaDataPatientCaseMetaData = {
  __typename?: "CaseMetaData";

  clinicId: Maybe<string>;

  etag: Maybe<string>;
};

export type RestorePatientCaseVariables = {
  id?: Maybe<string>;
  clinicId?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type RestorePatientCaseQuery = {
  __typename?: "Query";

  restorePatientCase: Maybe<RestorePatientCaseRestorePatientCase>;
};

export type RestorePatientCaseRestorePatientCase = {
  __typename?: "SavedCase";

  findings: Maybe<RestorePatientCaseFindings[]>;

  data: Maybe<RestorePatientCaseData>;

  etag: Maybe<string>;
};

export type RestorePatientCaseFindings = {
  __typename?: "SavedCaseGroup";

  features: Maybe<RestorePatientCaseFeatures[]>;

  group: Maybe<string>;
};

export type RestorePatientCaseFeatures = {
  __typename?: "GroupedFeatureResponse";

  feature: Maybe<RestorePatientCaseFeature>;

  group: Maybe<CaseFindingPresentationType>;

  source: Maybe<FindingSource>;
};

export type RestorePatientCaseFeature = QualifiedFeatureFragment;

export type RestorePatientCaseData = {
  __typename?: "OpaqueData";

  payload: Maybe<string>;
};

export type CheckForPiiVariables = {
  input?: Maybe<string>;
};

export type CheckForPiiQuery = {
  __typename?: "Query";

  checkForPII: Maybe<CheckForPiiCheckForPii>;
};

export type CheckForPiiCheckForPii = {
  __typename?: "UpdateSummaryResponse";

  status: Maybe<SavedCaseStatus>;

  errors: Maybe<UpdateSummaryErrors>;
};

export type StorePatientCaseVariables = {
  qfs?: Maybe<SavedCaseGroupInput[]>;
  data?: Maybe<OpaqueDataInput>;
  auto?: Maybe<boolean>;
  clinicId?: Maybe<string>;
  readOnly?: Maybe<boolean>;
};

export type StorePatientCaseMutation = {
  __typename?: "Mutation";

  storePatientCase: Maybe<string>;
};

export type UpdatePatientCaseVariables = {
  qfs?: Maybe<SavedCaseGroupInput[]>;
  data?: Maybe<OpaqueDataInput>;
  id?: Maybe<string>;
  etag?: Maybe<string>;
  clinicId?: Maybe<string>;
};

export type UpdatePatientCaseMutation = {
  __typename?: "Mutation";

  updatePatientCase: Maybe<UpdatePatientCaseUpdatePatientCase>;
};

export type UpdatePatientCaseUpdatePatientCase = {
  __typename?: "UpdateCaseResponse";

  status: Maybe<SavedCaseStatus>;

  etag: Maybe<string>;
};

export type UpdatePatientSummaryVariables = {
  caseId?: Maybe<string>;
  clinicId?: Maybe<string>;
  summaryInput?: Maybe<SummaryInput>;
};

export type UpdatePatientSummaryMutation = {
  __typename?: "Mutation";

  updateSummary: Maybe<UpdatePatientSummaryUpdateSummary>;
};

export type UpdatePatientSummaryUpdateSummary = {
  __typename?: "UpdateSummaryResponse";

  status: Maybe<SavedCaseStatus>;

  errors: Maybe<UpdateSummaryErrors>;

  etag: Maybe<string>;
};

export type CopyPatientCaseVariables = {
  id?: Maybe<string>;
  clinicId?: Maybe<string>;
};

export type CopyPatientCaseMutation = {
  __typename?: "Mutation";

  copyPatientCase: Maybe<string>;
};

export type StoreOpaqueDataVariables = {
  data?: Maybe<StoredData>;
  type?: Maybe<string>;
  auto?: Maybe<boolean>;
};

export type StoreOpaqueDataMutation = {
  __typename?: "Mutation";

  storeOpaqueData: Maybe<string>;
};

export type UpdateOpaqueDataVariables = {
  id?: Maybe<string>;
  data?: Maybe<StoredData>;
  type?: Maybe<string>;
};

export type UpdateOpaqueDataMutation = {
  __typename?: "Mutation";

  updateOpaqueData: Maybe<string>;
};

export type RestoreOpaqueDataVariables = {
  id?: Maybe<string>;
  type?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type RestoreOpaqueDataQuery = {
  __typename?: "Query";

  restoreOpaqueData: Maybe<RestoreOpaqueDataRestoreOpaqueData>;
};

export type RestoreOpaqueDataRestoreOpaqueData = {
  __typename?: "RestoredData";

  features: Maybe<RestoreOpaqueDataFeatures[]>;

  data: Maybe<OpaqueDataScalar>;
};

export type RestoreOpaqueDataFeatures = {
  __typename?: "GroupedFeatureResponse";

  feature: Maybe<RestoreOpaqueDataFeature>;

  group: Maybe<CaseFindingPresentationType>;

  skip: Maybe<boolean>;
};

export type RestoreOpaqueDataFeature = QualifiedFeatureFragment;

export type SendFeedbackVariables = {
  clinicName?: Maybe<string>;
  rating?: Maybe<number>;
  text?: Maybe<string>;
  sharedCase?: Maybe<string>;
  sharedCaseId?: Maybe<string>;
  urlPrefix?: Maybe<string>;
  sender?: Maybe<string>;
};

export type SendFeedbackMutation = {
  __typename?: "Mutation";

  sendFeedback: Maybe<boolean>;
};

export type GetPanelVariables = {
  id: string;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type GetPanelQuery = {
  __typename?: "Query";

  panel: Maybe<GetPanelPanel>;
};

export type GetPanelPanel = PanelFragment;

export type GetPanelWithChildrenVariables = {
  id: string;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type GetPanelWithChildrenQuery = {
  __typename?: "Query";

  panel: Maybe<GetPanelWithChildrenPanel>;
};

export type GetPanelWithChildrenPanel = {
  __typename?: "Panel";

  id: string;

  children: Maybe<GetPanelWithChildrenChildren[]>;
};

export type GetPanelWithChildrenChildren = PanelFragment;

export type SearchQualifiedFeaturesQueryVariables = {
  search?: Maybe<string>;
  categories?: Maybe<string[]>;
  gender?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  patientFacing?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  panelIds?: Maybe<string[]>;
  ancestors?: Maybe<string[]>;
  customPatientFacingPropertyValues?: Maybe<string[]>;
};

export type SearchQualifiedFeaturesQueryQuery = {
  __typename?: "Query";

  qualifiedFeatureSearch: Maybe<
    SearchQualifiedFeaturesQueryQualifiedFeatureSearch[]
  >;
};

export type SearchQualifiedFeaturesQueryQualifiedFeatureSearch = QualifiedFeatureFragment;

export type QualifierSearchQueryVariables = {
  feature: string;
  relation?: Maybe<string>;
  search?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type QualifierSearchQueryQuery = {
  __typename?: "Query";

  qualifierSearch: Maybe<QualifierSearchQueryQualifierSearch[]>;
};

export type QualifierSearchQueryQualifierSearch = QualifierFragment;

export type FreeTextToPatientCaseVariables = {
  text?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type FreeTextToPatientCaseQuery = {
  __typename?: "Query";

  freeTextToPatientCase: Maybe<FreeTextToPatientCaseFreeTextToPatientCase>;
};

export type FreeTextToPatientCaseFreeTextToPatientCase = {
  __typename?: "FreeTextToPatientResponse";

  patientFindings: Maybe<FreeTextToPatientCasePatientFindings[]>;

  debug: Maybe<StructuredDebugInfo>;
};

export type FreeTextToPatientCasePatientFindings = GroupedFeatureResponseFragment;

export type RunLoadersStatusQueryVariables = {
  env?: Maybe<string>;
};

export type RunLoadersStatusQueryQuery = {
  __typename?: "Query";

  runLoadersStatus: Maybe<RunLoadersStatusQueryRunLoadersStatus[]>;
};

export type RunLoadersStatusQueryRunLoadersStatus = {
  __typename?: "RunLoadersStatusResult";

  id: Maybe<string>;

  filter: Maybe<string>;

  startedat: Maybe<number>;

  shardtotal: Maybe<number>;

  version: Maybe<string>;

  commitserial: Maybe<number>;

  revision: Maybe<string>;

  codeversion: Maybe<number>;

  state: Maybe<GVersionState>;

  url: Maybe<string>;

  runningEnv: Maybe<string[]>;

  loaders: Maybe<RunLoadersStatusQueryLoaders[]>;
};

export type RunLoadersStatusQueryLoaders = {
  __typename?: "LoaderInfoResult";

  shard: Maybe<number>;

  shardtotal: Maybe<number>;

  processed: Maybe<number>;

  processedtotal: Maybe<number>;

  date: Maybe<number>;

  lastevent: Maybe<string>;

  state: Maybe<string>;
};

export type MetricsPreloadVariables = {
  env?: Maybe<string>;
  filter?: Maybe<string>;
  shards?: Maybe<number>;
  taskversion?: Maybe<string>;
};

export type MetricsPreloadMutation = {
  __typename?: "Mutation";

  metricsPreload: Maybe<MetricsPreloadMetricsPreload>;
};

export type MetricsPreloadMetricsPreload = {
  __typename?: "MetricsPreloadResult";

  success: Maybe<string>;
};

export type SetVersionStateVariables = {
  env?: Maybe<string>;
  version?: Maybe<string>;
  state?: Maybe<GVersionState>;
};

export type SetVersionStateMutation = {
  __typename?: "Mutation";

  setVersionState: Maybe<boolean>;
};

export type SetVersionRunningEnvVariables = {
  env?: Maybe<string>;
  version?: Maybe<string>;
  runningEnv?: Maybe<string[]>;
};

export type SetVersionRunningEnvMutation = {
  __typename?: "Mutation";

  setVersionRunningEnv: Maybe<boolean>;
};

export type MetricsQfVariables = {
  qfs?: Maybe<QualifiedFeatureInput[]>;
};

export type MetricsQfQuery = {
  __typename?: "Query";

  metricsQF: Maybe<MetricsQfMetricsQf[]>;
};

export type MetricsQfMetricsQf = {
  __typename?: "QFMetrics";

  id: Maybe<string>;

  incidence: Maybe<number>;

  prevalence: Maybe<number>;

  incidenceDescription: Maybe<string>;

  incidenceSource: Maybe<string>;

  prevalenceDescription: Maybe<string>;

  prevalenceSource: Maybe<string>;
};

export type QfsCorrelateVariables = {
  qfs1?: Maybe<QualifiedFeatureInput[]>;
  qfs2?: Maybe<QualifiedFeatureInput[]>;
};

export type QfsCorrelateQuery = {
  __typename?: "Query";

  qfsCorrelate: Maybe<QfsCorrelateQfsCorrelate>;
};

export type QfsCorrelateQfsCorrelate = {
  __typename?: "QFSCorrelate";

  qfs1: Maybe<QfsCorrelateQfs1>;

  qfs2: Maybe<QfsCorrelateQfs2>;

  correlate: Maybe<string>;
};

export type QfsCorrelateQfs1 = {
  __typename?: "QFDetails";

  name: Maybe<string>;
};

export type QfsCorrelateQfs2 = {
  __typename?: "QFDetails";

  name: Maybe<string>;
};

export type QfsCorrelateListsVariables = {
  qfsList1?: Maybe<QualifiedFeatureInput[][]>;
  qfsList2?: Maybe<QualifiedFeatureInput[][]>;
};

export type QfsCorrelateListsQuery = {
  __typename?: "Query";

  qfsCorrelateLists: Maybe<QfsCorrelateListsQfsCorrelateLists[]>;
};

export type QfsCorrelateListsQfsCorrelateLists = {
  __typename?: "QFSCorrelate";

  qfs1: Maybe<QfsCorrelateListsQfs1>;

  qfs2: Maybe<QfsCorrelateListsQfs2>;

  correlate: Maybe<string>;
};

export type QfsCorrelateListsQfs1 = {
  __typename?: "QFDetails";

  name: Maybe<string>;
};

export type QfsCorrelateListsQfs2 = {
  __typename?: "QFDetails";

  name: Maybe<string>;
};

export type LambdaTestsVariables = {
  devEnv?: Maybe<string>;
  project?: Maybe<string>;
  headline?: Maybe<string>;
  cases?: Maybe<string>;
  multiFilter?: Maybe<string>;
  tagsFilter?: Maybe<string>;
  taskVersion?: Maybe<string>;
  modelVersion?: Maybe<string>;
  filter?: Maybe<string>;
  panel?: Maybe<string>;
  keepFunctions?: Maybe<number>;
  disableLogs?: Maybe<number>;
  concurrency?: Maybe<number>;
  loops?: Maybe<string>;
};

export type LambdaTestsMutation = {
  __typename?: "Mutation";

  lambdaTests: Maybe<LambdaTestsLambdaTests>;
};

export type LambdaTestsLambdaTests = {
  __typename?: "LambdaTestResult";

  success: Maybe<string>;
};

export type CreateClinicVariables = {
  name?: Maybe<string>;
  config?: Maybe<JsonObject>;
  displayName?: Maybe<string>;
};

export type CreateClinicMutation = {
  __typename?: "Mutation";

  user: Maybe<CreateClinicUser>;
};

export type CreateClinicUser = {
  __typename?: "UserMutations";

  createClinic: Maybe<CreateClinicCreateClinic>;
};

export type CreateClinicCreateClinic = {
  __typename?: "ClinicData";

  id: Maybe<string>;

  name: Maybe<string>;

  config: Maybe<JsonObject>;

  displayName: Maybe<string>;
};

export type RenameClinicVariables = {
  id?: Maybe<string>;
  newName?: Maybe<string>;
  newDisplayName?: Maybe<string>;
};

export type RenameClinicMutation = {
  __typename?: "Mutation";

  clinic: Maybe<RenameClinicClinic>;
};

export type RenameClinicClinic = {
  __typename?: "ClinicMutations";

  renameClinic: Maybe<RenameClinicRenameClinic>;
};

export type RenameClinicRenameClinic = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type SetClinicConfigVariables = {
  id?: Maybe<string>;
  config?: Maybe<JsonObject>;
};

export type SetClinicConfigMutation = {
  __typename?: "Mutation";

  clinic: Maybe<SetClinicConfigClinic>;
};

export type SetClinicConfigClinic = {
  __typename?: "ClinicMutations";

  setConfig: Maybe<SetClinicConfigSetConfig>;
};

export type SetClinicConfigSetConfig = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type AddUserVariables = {
  clinicId?: Maybe<string>;
  user?: Maybe<string>;
  role?: Maybe<UserRoles>;
};

export type AddUserMutation = {
  __typename?: "Mutation";

  clinic: Maybe<AddUserClinic>;
};

export type AddUserClinic = {
  __typename?: "ClinicMutations";

  addUser: Maybe<AddUserAddUser>;
};

export type AddUserAddUser = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type DelUserVariables = {
  clinicId?: Maybe<string>;
  user?: Maybe<string>;
};

export type DelUserMutation = {
  __typename?: "Mutation";

  clinic: Maybe<DelUserClinic>;
};

export type DelUserClinic = {
  __typename?: "ClinicMutations";

  delUser: Maybe<DelUserDelUser>;
};

export type DelUserDelUser = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type GetUsersOfClinicVariables = {
  id?: Maybe<string>;
};

export type GetUsersOfClinicQuery = {
  __typename?: "Query";

  clinic: Maybe<GetUsersOfClinicClinic>;
};

export type GetUsersOfClinicClinic = {
  __typename?: "ClinicQueries";

  users: Maybe<GetUsersOfClinicUsers[]>;
};

export type GetUsersOfClinicUsers = {
  __typename?: "UserData";

  email: Maybe<string>;

  role: Maybe<UserRoles>;
};

export type GetUserDataVariables = {
  id?: Maybe<string>;
};

export type GetUserDataQuery = {
  __typename?: "Query";

  clinic: Maybe<GetUserDataClinic>;
};

export type GetUserDataClinic = {
  __typename?: "ClinicQueries";

  userData: Maybe<GetUserDataUserData>;
};

export type GetUserDataUserData = {
  __typename?: "UserData";

  email: Maybe<string>;

  role: Maybe<UserRoles>;
};

export type UserClinicsVariables = {
  onlyMyClinics?: Maybe<boolean>;
};

export type UserClinicsQuery = {
  __typename?: "Query";

  user: Maybe<UserClinicsUser>;
};

export type UserClinicsUser = {
  __typename?: "UserQueries";

  clinics: Maybe<UserClinicsClinics[]>;
};

export type UserClinicsClinics = {
  __typename?: "ClinicData";

  config: Maybe<JsonObject>;

  id: Maybe<string>;

  name: Maybe<string>;

  displayName: Maybe<string>;
};

export type IsClinicNameAvailableVariables = {
  name?: Maybe<string>;
};

export type IsClinicNameAvailableQuery = {
  __typename?: "Query";

  user: Maybe<IsClinicNameAvailableUser>;
};

export type IsClinicNameAvailableUser = {
  __typename?: "UserQueries";

  isClinicNameAvailable: Maybe<boolean>;
};

export type RecentCodesVariables = {
  id?: Maybe<string>;
  daysPrior?: Maybe<number>;
  limit?: Maybe<number>;
  isFinished?: Maybe<boolean>;
  exclusiveStartId?: Maybe<string>;
  exclusiveStartDate?: Maybe<number>;
};

export type RecentCodesQuery = {
  __typename?: "Query";

  clinic: Maybe<RecentCodesClinic>;
};

export type RecentCodesClinic = {
  __typename?: "ClinicQueries";

  recentCodes: Maybe<RecentCodesRecentCodes[]>;
};

export type RecentCodesRecentCodes = {
  __typename?: "ShareCodeData";

  patientProperties: Maybe<PatientProperties>;

  visitStatus: Maybe<VisitStatus>;
} & ShareCodeDataFragmentFragment;

export type RecentVisitsVariables = {
  id?: Maybe<string>;
  limit?: Maybe<number>;
  isFinished?: Maybe<boolean>;
  startKey?: Maybe<StartKey>;
  order?: Maybe<Order>;
};

export type RecentVisitsQuery = {
  __typename?: "Query";

  clinic: Maybe<RecentVisitsClinic>;
};

export type RecentVisitsClinic = {
  __typename?: "ClinicQueries";

  recentVisits: Maybe<RecentVisitsRecentVisits>;
};

export type RecentVisitsRecentVisits = {
  __typename?: "Visits";

  visits: Maybe<RecentVisitsVisits[]>;

  startKey: Maybe<StartKey>;
};

export type RecentVisitsVisits = {
  __typename?: "ShareCodeData";

  patientProperties: Maybe<PatientProperties>;

  visitStatus: Maybe<VisitStatus>;
} & ShareCodeDataFragmentFragment;

export type FilteredVisitsVariables = {
  id?: Maybe<string>;
  filter?: Maybe<FilterInput>;
  startKey?: Maybe<StartKey>;
  sortKey?: Maybe<string>;
  limitRequested?: Maybe<number>;
  isFinished?: Maybe<boolean>;
  order?: Maybe<Order>;
};

export type FilteredVisitsQuery = {
  __typename?: "Query";

  clinic: Maybe<FilteredVisitsClinic>;
};

export type FilteredVisitsClinic = {
  __typename?: "ClinicQueries";

  filteredVisits: Maybe<FilteredVisitsFilteredVisits>;
};

export type FilteredVisitsFilteredVisits = {
  __typename?: "Visits";

  visits: Maybe<FilteredVisitsVisits[]>;

  startKey: Maybe<StartKey>;

  count: Maybe<number>;
};

export type FilteredVisitsVisits = {
  __typename?: "ShareCodeData";

  visitStatus: Maybe<VisitStatus>;

  visitStatusStructure: Maybe<FilteredVisitsVisitStatusStructure>;

  patientProperties: Maybe<PatientProperties>;
} & ShareCodeDataFragmentFragment;

export type FilteredVisitsVisitStatusStructure = VisitStatusFragmentFragment;

export type GetCodeVariables = {
  id?: Maybe<string>;
  code?: Maybe<string>;
};

export type GetCodeQuery = {
  __typename?: "Query";

  clinic: Maybe<GetCodeClinic>;
};

export type GetCodeClinic = {
  __typename?: "ClinicQueries";

  getCodeByDigits: Maybe<GetCodeGetCodeByDigits>;
};

export type GetCodeGetCodeByDigits = {
  __typename?: "ShareCodeData";

  visitStatusStructure: Maybe<GetCodeVisitStatusStructure>;

  patientProperties: Maybe<PatientProperties>;
} & ShareCodeDataFragmentFragment;

export type GetCodeVisitStatusStructure = VisitStatusFragmentFragment;

export type GetCodeByIdVariables = {
  clinicId?: Maybe<string>;
  shareId?: Maybe<string>;
  ehrid?: Maybe<string>;
};

export type GetCodeByIdQuery = {
  __typename?: "Query";

  clinic: Maybe<GetCodeByIdClinic>;
};

export type GetCodeByIdClinic = {
  __typename?: "ClinicQueries";

  getCodeById: Maybe<GetCodeByIdGetCodeById>;
};

export type GetCodeByIdGetCodeById = {
  __typename?: "ShareCodeData";

  id: Maybe<string>;

  digitCode: Maybe<string>;

  shareid: Maybe<string>;

  date: Maybe<number>;
};

export type DeleteCodeVariables = {
  clinicId?: Maybe<string>;
  code?: Maybe<string>;
};

export type DeleteCodeMutation = {
  __typename?: "Mutation";

  clinic: Maybe<DeleteCodeClinic>;
};

export type DeleteCodeClinic = {
  __typename?: "ClinicMutations";

  deleteCode: Maybe<DeleteCodeDeleteCode>;
};

export type DeleteCodeDeleteCode = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type AddUserCommentVariables = {
  clinicId?: Maybe<string>;
  code?: Maybe<string>;
  comment?: Maybe<string>;
};

export type AddUserCommentMutation = {
  __typename?: "Mutation";

  clinic: Maybe<AddUserCommentClinic>;
};

export type AddUserCommentClinic = {
  __typename?: "ClinicMutations";

  addUserComment: Maybe<AddUserCommentAddUserComment>;
};

export type AddUserCommentAddUserComment = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type ClinicDataVariables = {
  id?: Maybe<string>;
};

export type ClinicDataQuery = {
  __typename?: "Query";

  clinic: Maybe<ClinicDataClinic>;
};

export type ClinicDataClinic = {
  __typename?: "ClinicQueries";

  data: Maybe<ClinicDataData>;
};

export type ClinicDataData = {
  __typename?: "ClinicData";

  config: Maybe<JsonObject>;

  mergedConfiguration: Maybe<JsonObject>;

  name: Maybe<string>;

  id: Maybe<string>;

  displayName: Maybe<string>;
};

export type GetClinicConfigVariables = {
  id?: Maybe<string>;
};

export type GetClinicConfigQuery = {
  __typename?: "Query";

  clinic: Maybe<GetClinicConfigClinic>;
};

export type GetClinicConfigClinic = {
  __typename?: "ClinicQueries";

  data: Maybe<GetClinicConfigData>;
};

export type GetClinicConfigData = {
  __typename?: "ClinicData";

  config: Maybe<JsonObject>;

  mergedConfiguration: Maybe<JsonObject>;

  name: Maybe<string>;
};

export type ClinicByNameVariables = {
  name?: Maybe<string>;
};

export type ClinicByNameQuery = {
  __typename?: "Query";

  clinicByName: Maybe<ClinicByNameClinicByName>;
};

export type ClinicByNameClinicByName = {
  __typename?: "ClinicQueries";

  data: Maybe<ClinicByNameData>;
};

export type ClinicByNameData = {
  __typename?: "ClinicData";

  config: Maybe<JsonObject>;

  mergedConfiguration: Maybe<JsonObject>;

  name: Maybe<string>;

  displayName: Maybe<string>;

  id: Maybe<string>;
};

export type MakeClinicShortCodeVariables = {
  id?: Maybe<string>;
  shareid?: Maybe<string>;
};

export type MakeClinicShortCodeMutation = {
  __typename?: "Mutation";

  clinic: Maybe<MakeClinicShortCodeClinic>;
};

export type MakeClinicShortCodeClinic = {
  __typename?: "ClinicMutations";

  updateFinished: Maybe<MakeClinicShortCodeUpdateFinished>;
};

export type MakeClinicShortCodeUpdateFinished = ShareCodeDataFragmentFragment;

export type CopyCaseToClinicVariables = {
  clinicId?: Maybe<string>;
  caseId?: Maybe<string>;
  destClinicId?: Maybe<string>;
};

export type CopyCaseToClinicMutation = {
  __typename?: "Mutation";

  clinic: Maybe<CopyCaseToClinicClinic>;
};

export type CopyCaseToClinicClinic = {
  __typename?: "ClinicMutations";

  copyCaseToClinic: Maybe<CopyCaseToClinicCopyCaseToClinic>;
};

export type CopyCaseToClinicCopyCaseToClinic = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type UpdateSeenByUserVariables = {
  clinicId?: Maybe<string>;
  code?: Maybe<string>;
  user?: Maybe<string>;
  isSeen?: Maybe<boolean>;
};

export type UpdateSeenByUserMutation = {
  __typename?: "Mutation";

  clinic: Maybe<UpdateSeenByUserClinic>;
};

export type UpdateSeenByUserClinic = {
  __typename?: "ClinicMutations";

  updateSeenByUser: Maybe<UpdateSeenByUserUpdateSeenByUser>;
};

export type UpdateSeenByUserUpdateSeenByUser = ShareCodeDataFragmentFragment;

export type DeleteClinicVariables = {
  id?: Maybe<string>;
};

export type DeleteClinicMutation = {
  __typename?: "Mutation";

  clinic: Maybe<DeleteClinicClinic>;
};

export type DeleteClinicClinic = {
  __typename?: "ClinicMutations";

  deleteClinic: Maybe<DeleteClinicDeleteClinic>;
};

export type DeleteClinicDeleteClinic = {
  __typename?: "Result";

  status: Maybe<string>;
};

export type PatientSummaryByCaseVariables = {
  patientCase?: Maybe<PatientCaseInput>;
  diseases?: Maybe<QualifiedFeatureInput[]>;
  suggestions?: Maybe<QualifiedFeatureInput[]>;
  options?: Maybe<SummaryOptions>;
  summaryOptions?: Maybe<SummaryOptionsInput>;
};

export type PatientSummaryByCaseQuery = {
  __typename?: "Query";

  patientSummaryByCase: Maybe<PatientSummaryByCasePatientSummaryByCase>;
};

export type PatientSummaryByCasePatientSummaryByCase = {
  __typename?: "Summary";

  data: Maybe<PatientSummaryByCaseData>;
};

export type PatientSummaryByCaseData = PatientFirstSummaryFragment;

export type MetricsPathsVariables = {
  causeids?: Maybe<string[]>;
  effectids?: Maybe<string[]>;
  causeqfs?: Maybe<QualifiedFeatureInput[]>;
  effectqfs?: Maybe<QualifiedFeatureInput[]>;
  effectIsDisease?: Maybe<boolean>;
  patient?: Maybe<PatientCaseInput>;
};

export type MetricsPathsQuery = {
  __typename?: "Query";

  metricsPaths: Maybe<MetricsPathsMetricsPaths[]>;
};

export type MetricsPathsMetricsPaths = {
  __typename?: "MetricsPath";

  causeId: Maybe<string>;

  effectId: Maybe<string>;

  nodes: Maybe<MetricsPathsNodes[]>;

  edges: Maybe<MetricsPathsEdges[]>;

  prob: Maybe<number>;
};

export type MetricsPathsNodes = {
  __typename?: "MetricsNode";

  name: Maybe<string>;

  desc: Maybe<string>;
};

export type MetricsPathsEdges = {
  __typename?: "MetricsEdge";

  type: Maybe<RuleType>;

  prob: Maybe<number>;

  incidence: Maybe<number>;

  lift: Maybe<number>;

  stats: Maybe<MetricsPathsStats[]>;
};

export type MetricsPathsStats = {
  __typename?: "MetricsEdgeStat";

  rate: Maybe<number>;

  unit: Maybe<MetricsRuleUnits>;

  lastCommitDate: Maybe<number>;

  ruleValue: Maybe<MetricsPathsRuleValue>;
};

export type MetricsPathsRuleValue = {
  __typename?: "RuleValue";

  id: string;

  ruleValueId: Maybe<string>;

  type: Maybe<RuleType>;

  citationsObj: Maybe<MetricsPathsCitationsObj>;

  exactCitation: Maybe<boolean>;
};

export type MetricsPathsCitationsObj = {
  __typename?: "CitationData";

  id: string;

  vancouver: Maybe<string>;
};

export type UndoConversationVariables = {
  context?: Maybe<ConversationContext>;
  debug?: Maybe<boolean>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type UndoConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<UndoConversationConversation>;
};

export type UndoConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<UndoConversationState>;
};

export type UndoConversationState = {
  __typename?: "ConversationState";

  undo: Maybe<UndoConversationUndo>;
};

export type UndoConversationUndo = {
  __typename?: "ConversationState";

  gender: Maybe<string>;

  entropy: Maybe<number>;

  context: Maybe<ConversationContext>;

  nextSuggestions: Maybe<UndoConversationNextSuggestions>;
};

export type UndoConversationNextSuggestions = ConversationNextSuggestionsFragment;

export type AbandonedPatientConversationVariables = {
  context?: Maybe<ConversationContext>;
  debug?: Maybe<boolean>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  saveToCase?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  abandonedStatus?: Maybe<SubStatus>;
};

export type AbandonedPatientConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<AbandonedPatientConversationConversation>;
};

export type AbandonedPatientConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<AbandonedPatientConversationState>;
};

export type AbandonedPatientConversationState = {
  __typename?: "ConversationState";

  abandoned: Maybe<AbandonedPatientConversationAbandoned>;
};

export type AbandonedPatientConversationAbandoned = ConversationStateFragment &
  ConversationStateNextResponseFragment;

export type GetRecommendationsVariables = {
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  saveToCase?: Maybe<boolean>;
  patientCaseId?: Maybe<string>;
  clinicId?: Maybe<string>;
};

export type GetRecommendationsQuery = {
  __typename?: "Query";

  conversation: Maybe<GetRecommendationsConversation>;
};

export type GetRecommendationsConversation = {
  __typename?: "ConversationQueries";

  start: Maybe<GetRecommendationsStart>;
};

export type GetRecommendationsStart = {
  __typename?: "ConversationState";

  recommendations: Maybe<GetRecommendationsRecommendations[]>;

  navigationRecommendation: Maybe<GetRecommendationsNavigationRecommendation>;
};

export type GetRecommendationsRecommendations = ConversationRecommendationFragment;

export type GetRecommendationsNavigationRecommendation = ConversationRecommendationFragment;

export type NextResponseVariables = {
  context?: Maybe<ConversationContext>;
  debug?: Maybe<boolean>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  saveToCase?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type NextResponseQuery = {
  __typename?: "Query";

  conversation: Maybe<NextResponseConversation>;
} & BackendModelInfoFragment;

export type NextResponseConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<NextResponseState>;
};

export type NextResponseState = ConversationStateNextResponseFragment;

export type AddPatientAnswerConversationVariables = {
  context?: Maybe<ConversationContext>;
  answer?: Maybe<ConversationAnswer>;
  debug?: Maybe<boolean>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  saveToCase?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type AddPatientAnswerConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<AddPatientAnswerConversationConversation>;
} & BackendModelInfoFragment;

export type AddPatientAnswerConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<AddPatientAnswerConversationState>;
};

export type AddPatientAnswerConversationState = {
  __typename?: "ConversationState";

  addAnswer: Maybe<AddPatientAnswerConversationAddAnswer>;
};

export type AddPatientAnswerConversationAddAnswer = ConversationStateNextResponseFragment;

export type DoPatientExchangeVariables = {
  context?: Maybe<ConversationContext>;
  patient?: Maybe<GroupedFeature[]>;
  patientVolunteered?: Maybe<number[]>;
  missingInCase?: Maybe<MissingInCaseAction>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type DoPatientExchangeQuery = {
  __typename?: "Query";

  conversation: Maybe<DoPatientExchangeConversation>;
};

export type DoPatientExchangeConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<DoPatientExchangeState>;
};

export type DoPatientExchangeState = {
  __typename?: "ConversationState";

  doExchange: Maybe<DoPatientExchangeDoExchange>;
};

export type DoPatientExchangeDoExchange = ConversationExchangeResultFragment;

export type DoPatientExchangeResultVariables = {
  context?: Maybe<ConversationContext>;
  patient?: Maybe<GroupedFeature[]>;
  patientVolunteered?: Maybe<number[]>;
  missingInCase?: Maybe<MissingInCaseAction>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type DoPatientExchangeResultQuery = {
  __typename?: "Query";

  conversation: Maybe<DoPatientExchangeResultConversation>;
};

export type DoPatientExchangeResultConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<DoPatientExchangeResultState>;
};

export type DoPatientExchangeResultState = {
  __typename?: "ConversationState";

  entropy: Maybe<number>;

  doExchange: Maybe<DoPatientExchangeResultDoExchange>;

  ddClusters: Maybe<DoPatientExchangeResultDdClusters>;

  urgent: Maybe<DoPatientExchangeResultUrgent>;

  nextSuggestions: Maybe<DoPatientExchangeResultNextSuggestions>;
};

export type DoPatientExchangeResultDoExchange = ConversationExchangeResultFragment;

export type DoPatientExchangeResultDdClusters = {
  __typename?: "DDResponse";

  clustersToConsider: Maybe<DoPatientExchangeResultClustersToConsider[]>;

  clinicalReasoning: Maybe<DoPatientExchangeResultClinicalReasoning[]>;
};

export type DoPatientExchangeResultClustersToConsider = {
  __typename?: "DiseaseCluster";

  title: Maybe<DoPatientExchangeResultTitle>;

  crIds: Maybe<string[]>;
};

export type DoPatientExchangeResultTitle = {
  __typename?: "QualifiedFeature";

  name: string;
};

export type DoPatientExchangeResultClinicalReasoning = {
  __typename?: "ClinicalReasoning";

  id: string;

  disease: Maybe<DoPatientExchangeResultDisease[]>;

  scores: Maybe<DoPatientExchangeResultScores[]>;

  urgency: Maybe<number>;
};

export type DoPatientExchangeResultDisease = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  qualifiers: Maybe<DoPatientExchangeResultQualifiers[]>;
};

export type DoPatientExchangeResultQualifiers = {
  __typename?: "Qualifier";

  name: string;
};

export type DoPatientExchangeResultScores = {
  __typename?: "ScoreByType";

  postClusterAdjustment: Maybe<number>;

  sortType: Maybe<SortType>;
};

export type DoPatientExchangeResultUrgent = {
  __typename?: "DDUrgency";

  urgencyLevelId: Maybe<string>;

  urgent: Maybe<boolean>;
};

export type DoPatientExchangeResultNextSuggestions = {
  __typename?: "PatientSuggestions";

  diseases: Maybe<DoPatientExchangeResultDiseases[]>;

  suggestions: Maybe<DoPatientExchangeResultSuggestions[]>;
};

export type DoPatientExchangeResultDiseases = {
  __typename?: "DiseaseForPlan";

  disease: Maybe<DoPatientExchangeResult_Disease>;

  prob: Maybe<number>;
};

export type DoPatientExchangeResult_Disease = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;
};

export type DoPatientExchangeResultSuggestions = {
  __typename?: "PatientSuggestion";

  perDiseaseMetrics: Maybe<DoPatientExchangeResultPerDiseaseMetrics[]>;

  suggestion: Maybe<DoPatientExchangeResult_Suggestion>;

  findingEntropy: Maybe<number>;

  findingLeak: Maybe<number>;

  findingProbability: Maybe<number>;

  category: Maybe<DiseaseCategoryType>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;
};

export type DoPatientExchangeResultPerDiseaseMetrics = {
  __typename?: "SuggestionDiseaseMetric";

  suggestion: Maybe<DoPatientExchangeResultSuggestion>;

  disease: Maybe<DoPatientExchangeResult__Disease>;

  findingGivenDisease: Maybe<number>;
};

export type DoPatientExchangeResultSuggestion = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type DoPatientExchangeResult__Disease = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type DoPatientExchangeResult_Suggestion = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  qualifiers: Maybe<DoPatientExchangeResult_Qualifiers[]>;
};

export type DoPatientExchangeResult_Qualifiers = {
  __typename?: "Qualifier";

  name: string;

  relation: DoPatientExchangeResultRelation;

  range: Maybe<DoPatientExchangeResultRange>;
};

export type DoPatientExchangeResultRelation = {
  __typename?: "Relation";

  id: string;
};

export type DoPatientExchangeResultRange = {
  __typename?: "MeasurementRange";

  low: Maybe<number>;

  high: Maybe<number>;

  units: Maybe<MeasurementUnits>;
};

export type BuildCaseFromConversationVariables = {
  context?: Maybe<ConversationContext>;
  buildCaseOptions?: Maybe<BuildCaseOptions>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  caseId?: Maybe<string>;
};

export type BuildCaseFromConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<BuildCaseFromConversationConversation>;
};

export type BuildCaseFromConversationConversation = {
  __typename?: "ConversationQueries";

  buildSavedCase: Maybe<BuildCaseFromConversationBuildSavedCase>;
};

export type BuildCaseFromConversationBuildSavedCase = {
  __typename?: "SavedCaseResponse";

  findings: Maybe<BuildCaseFromConversationFindings[]>;
};

export type BuildCaseFromConversationFindings = GroupedFeatureResponseFragment;

export type SaveAsPatientConversationVariables = {
  context?: Maybe<ConversationContext>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  readOnly?: Maybe<boolean>;
};

export type SaveAsPatientConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<SaveAsPatientConversationConversation>;
};

export type SaveAsPatientConversationConversation = {
  __typename?: "ConversationQueries";

  saveAsNewCase: Maybe<SaveAsPatientConversationSaveAsNewCase>;
};

export type SaveAsPatientConversationSaveAsNewCase = {
  __typename?: "ConversationState";

  caseId: Maybe<string>;
};

export type PersistConversationVariables = {
  context?: Maybe<ConversationContext>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type PersistConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<PersistConversationConversation>;
};

export type PersistConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<PersistConversationState>;
};

export type PersistConversationState = {
  __typename?: "ConversationState";

  context: Maybe<ConversationContext>;
};

export type PersistConversationByIdVariables = {
  existingCaseId?: Maybe<string>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type PersistConversationByIdQuery = {
  __typename?: "Query";

  conversation: Maybe<PersistConversationByIdConversation>;
};

export type PersistConversationByIdConversation = {
  __typename?: "ConversationQueries";

  start: Maybe<PersistConversationByIdStart>;
};

export type PersistConversationByIdStart = {
  __typename?: "ConversationState";

  persist: Maybe<PersistConversationByIdPersist>;
};

export type PersistConversationByIdPersist = {
  __typename?: "ConversationState";

  context: Maybe<ConversationContext>;
};

export type StartConversationVariables = {
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  patientCaseId?: Maybe<string>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  clinicId?: Maybe<string>;
};

export type StartConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<StartConversationConversation>;
} & BackendModelInfoFragment;

export type StartConversationConversation = {
  __typename?: "ConversationQueries";

  start: Maybe<StartConversationStart>;
};

export type StartConversationStart = {
  __typename?: "ConversationState";

  caseId: Maybe<string>;

  exchanges: Maybe<StartConversationExchanges[]>;
} & (ConversationStateNextResponseFragment & ConversationStateFragment);

export type StartConversationExchanges = ConversationExchangeFragment;

export type PatientSummaryVariables = {
  id?: Maybe<string>;
  options?: Maybe<SummaryOptions>;
  summaryOptions?: Maybe<SummaryOptionsInput>;
  conversationOptions?: Maybe<ConversationOptions>;
  conversationOptionsInput?: Maybe<ConversationOptionsInput>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  clinicId?: Maybe<string>;
};

export type PatientSummaryQuery = {
  __typename?: "Query";

  patientSummary: Maybe<PatientSummaryPatientSummary>;
};

export type PatientSummaryPatientSummary = {
  __typename?: "Summary";

  data: Maybe<PatientSummaryData>;

  recommendations: Maybe<PatientSummaryRecommendations[]>;

  navigationAdvice: Maybe<PatientSummaryNavigationAdvice>;

  possibleCause: Maybe<PatientSummaryPossibleCause[]>;

  possibleCauses: Maybe<PatientSummaryPossibleCauses>;

  problemList: Maybe<PatientSummaryProblemList[]>;

  assessment: Maybe<PatientSummaryAssessment>;

  workup: Maybe<PatientSummaryWorkup>;

  workupList: Maybe<PatientSummaryWorkupList[]>;

  workupActionItems: Maybe<PatientSummaryWorkupActionItems>;

  validCase: Maybe<PatientSummaryValidCase>;

  status: Maybe<string>;

  caseEtag: Maybe<string>;
};

export type PatientSummaryData = PatientFirstSummaryFragment;

export type PatientSummaryRecommendations = ConversationRecommendationFragment;

export type PatientSummaryNavigationAdvice = ConversationRecommendationFragment;

export type PatientSummaryPossibleCause = SummaryDiseaseClusterFragment;

export type PatientSummaryPossibleCauses = {
  __typename?: "PossibleCauses";

  possibleCauses: Maybe<PatientSummary_PossibleCauses[]>;

  etag: Maybe<string>;
};

export type PatientSummary_PossibleCauses = SummaryDiseaseClusterFragment;

export type PatientSummaryProblemList = SummaryProblemFragment;

export type PatientSummaryAssessment = GroundedSummaryTextFragment;

export type PatientSummaryWorkup = SummaryTextFragment;

export type PatientSummaryWorkupList = {
  __typename?: "WorkupItem";

  category: Maybe<string>;

  title: Maybe<string>;
};

export type PatientSummaryWorkupActionItems = {
  __typename?: "WorkupActionItems";

  items: Maybe<PatientSummaryItems[]>;
} & GeneratedOutputFragment;

export type PatientSummaryItems = {
  __typename?: "WorkupItem";

  category: Maybe<string>;

  title: Maybe<string>;
};

export type PatientSummaryValidCase = {
  __typename?: "ValidCaseInfo";

  isValid: Maybe<boolean>;

  reason: Maybe<string>;

  etag: Maybe<string>;
};

export type PatientSummaryByContextVariables = {
  context?: Maybe<ConversationContext>;
  options?: Maybe<SummaryOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type PatientSummaryByContextQuery = {
  __typename?: "Query";

  conversation: Maybe<PatientSummaryByContextConversation>;
};

export type PatientSummaryByContextConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<PatientSummaryByContextState>;
};

export type PatientSummaryByContextState = {
  __typename?: "ConversationState";

  summary: Maybe<PatientSummaryByContextSummary>;

  navigationRecommendation: Maybe<
    PatientSummaryByContextNavigationRecommendation
  >;
};

export type PatientSummaryByContextSummary = PatientFirstSummaryFragment;

export type PatientSummaryByContextNavigationRecommendation = ConversationRecommendationFragment;

export type ConversationNextQuestionVariables = {
  context?: Maybe<ConversationContext>;
  endExchangeIndex?: Maybe<number>;
  qfLight?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  panel?: Maybe<string>;
};

export type ConversationNextQuestionQuery = {
  __typename?: "Query";

  conversation: Maybe<ConversationNextQuestionConversation>;
};

export type ConversationNextQuestionConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<ConversationNextQuestionState>;
};

export type ConversationNextQuestionState = ConversationStateNextQFragment;

export type RestoreConversationVariables = {
  context?: Maybe<ConversationContext>;
  qfLight?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  privateMode?: Maybe<boolean>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type RestoreConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<RestoreConversationConversation>;
} & BackendModelInfoFragment;

export type RestoreConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<RestoreConversationState>;
};

export type RestoreConversationState = {
  __typename?: "ConversationState";

  initialMessages: Maybe<RestoreConversationInitialMessages[]>;

  exchanges: Maybe<RestoreConversationExchanges[]>;
} & (ConversationStateNextResponseFragment & ConversationStateFragment);

export type RestoreConversationInitialMessages = ConversationInfoMessageFragment;

export type RestoreConversationExchanges = ConversationExchangeFragment;

export type DebugForLastQuestionVariables = {
  context?: Maybe<ConversationContext>;
  qfLight?: Maybe<boolean>;
  debug?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  privateMode?: Maybe<boolean>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
  endExchangeIndex?: Maybe<number>;
};

export type DebugForLastQuestionQuery = {
  __typename?: "Query";

  conversation: Maybe<DebugForLastQuestionConversation>;
} & BackendModelInfoFragment;

export type DebugForLastQuestionConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<DebugForLastQuestionState>;
};

export type DebugForLastQuestionState = {
  __typename?: "ConversationState";

  initialMessages: Maybe<DebugForLastQuestionInitialMessages[]>;

  exchanges: Maybe<DebugForLastQuestionExchanges[]>;

  debugInfo: Maybe<StructuredDebugInfo>;
} & (ConversationStateNextResponseFragment & ConversationStateFragment);

export type DebugForLastQuestionInitialMessages = ConversationInfoMessageFragment;

export type DebugForLastQuestionExchanges = ConversationExchangeFragment;

export type DdFromConversationVariables = {
  context?: Maybe<ConversationContext>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  options?: Maybe<ConversationOptions>;
  conversationOptions?: Maybe<ConversationOptionsInput>;
};

export type DdFromConversationQuery = {
  __typename?: "Query";

  conversation: Maybe<DdFromConversationConversation>;
};

export type DdFromConversationConversation = {
  __typename?: "ConversationQueries";

  state: Maybe<DdFromConversationState>;
};

export type DdFromConversationState = {
  __typename?: "ConversationState";

  primaryDDClusters: Maybe<string[]>;

  ddClusters: Maybe<DdFromConversationDdClusters>;

  urgent: Maybe<DdFromConversationUrgent>;

  navigationRecommendation: Maybe<DdFromConversationNavigationRecommendation>;
};

export type DdFromConversationDdClusters = {
  __typename?: "DDResponse";

  debugReport: Maybe<string[]>;
} & ConversationDdFragment;

export type DdFromConversationUrgent = {
  __typename?: "DDUrgency";

  urgencyLevelId: Maybe<string>;
};

export type DdFromConversationNavigationRecommendation = ConversationRecommendationFragment;

export type BackendModelVariables = {
  debug?: Maybe<boolean>;
};

export type BackendModelQuery = BackendModelInfoFragment;

export type FeatureByIdVariables = {
  id: string;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type FeatureByIdQuery = {
  __typename?: "Query";

  feature: Maybe<FeatureByIdFeature>;
};

export type FeatureByIdFeature = FeatureFragment;

export type GetPoliciesVariables = {};

export type GetPoliciesQuery = {
  __typename?: "Query";

  policies: Maybe<GetPoliciesPolicies[]>;
};

export type GetPoliciesPolicies = {
  __typename?: "Policy";

  id: Maybe<string>;

  name: Maybe<string>;
};

export type GetPanelsVariables = {};

export type GetPanelsQuery = {
  __typename?: "Query";

  panels: Maybe<GetPanelsPanels[]>;
};

export type GetPanelsPanels = {
  __typename?: "Panel";

  id: string;

  name: string;
};

export type GetGraphVariables = {
  root?: Maybe<QualifiedFeatureInput>;
  nodes?: Maybe<GroupedFeature[]>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type GetGraphQuery = {
  __typename?: "Query";

  graph: Maybe<GetGraphGraph>;
};

export type GetGraphGraph = {
  __typename?: "GraphResponse";

  graph: Maybe<GetGraph_Graph>;
};

export type GetGraph_Graph = PairGraphFragment;

export type GetGraphByIdsVariables = {
  root?: Maybe<string>;
  active?: Maybe<string[]>;
  background?: Maybe<string[]>;
  subtypes?: Maybe<string[]>;
  simplify?: Maybe<boolean>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type GetGraphByIdsQuery = {
  __typename?: "Query";

  graphByCohorts: Maybe<GetGraphByIdsGraphByCohorts>;
};

export type GetGraphByIdsGraphByCohorts = {
  __typename?: "GraphResponse";

  graph: Maybe<GetGraphByIdsGraph>;
};

export type GetGraphByIdsGraph = PairGraphFragment;

export type GroundTextVariables = {
  text: string;
  caseId?: Maybe<string>;
};

export type GroundTextQuery = {
  __typename?: "Query";

  groundText: Maybe<GroundTextGroundText>;
};

export type GroundTextGroundText = GroundingFragment;

export type DrByFeatureVariables = {
  featureid: string;
  category?: Maybe<string>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  withCitations?: Maybe<boolean>;
};

export type DrByFeatureQuery = {
  __typename?: "Query";

  reference: Maybe<DrByFeatureReference>;
};

export type DrByFeatureReference = {
  __typename?: "ReferenceQueries";

  disease: Maybe<DrByFeatureDisease>;
};

export type DrByFeatureDisease = {
  __typename?: "DiseaseReference";

  citationsDVData: Maybe<DrByFeatureCitationsDvData[]>;
} & DiseaseReferenceDataFragment;

export type DrByFeatureCitationsDvData = {
  __typename?: "CitationDiseaseData";

  citationId: string;

  count: Maybe<number>;

  findings: Maybe<DrByFeatureFindings[]>;

  subtypes: Maybe<DrByFeatureSubtypes[]>;
};

export type DrByFeatureFindings = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<DrByFeatureQfs[]>;
};

export type DrByFeatureQfs = QualifiedFeatureFragment;

export type DrByFeatureSubtypes = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<DrByFeature_Qfs[]>;
};

export type DrByFeature_Qfs = QualifiedFeatureFragment;

export type DrOverviewByFeatureVariables = {
  featureid: string;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
};

export type DrOverviewByFeatureQuery = {
  __typename?: "Query";

  reference: Maybe<DrOverviewByFeatureReference>;
};

export type DrOverviewByFeatureReference = {
  __typename?: "ReferenceQueries";

  disease: Maybe<DrOverviewByFeatureDisease>;
};

export type DrOverviewByFeatureDisease = DiseaseReferenceOverviewDataFragment;

export type DrAndFindingPairVariables = {
  featureid: string;
  finding?: Maybe<QualifiedFeatureInput>;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  withCitations?: Maybe<boolean>;
};

export type DrAndFindingPairQuery = {
  __typename?: "Query";

  reference: Maybe<DrAndFindingPairReference>;
};

export type DrAndFindingPairReference = {
  __typename?: "ReferenceQueries";

  diseaseWithFinding: Maybe<DrAndFindingPairDiseaseWithFinding>;
};

export type DrAndFindingPairDiseaseWithFinding = DiseaseReferenceDataFragment;

export type DrAndCohortPairVariables = {
  diseaseCohortid: string;
  cohortid: string;
  qfLight?: Maybe<boolean>;
  includeFeatureRange?: Maybe<boolean>;
  withCitations?: Maybe<boolean>;
};

export type DrAndCohortPairQuery = {
  __typename?: "Query";

  reference: Maybe<DrAndCohortPairReference>;
};

export type DrAndCohortPairReference = {
  __typename?: "ReferenceQueries";

  diseaseWithFinding: Maybe<DrAndCohortPairDiseaseWithFinding>;
};

export type DrAndCohortPairDiseaseWithFinding = DiseaseReferenceDataFragment;

export type MeasurementRangeFragment = {
  __typename?: "MeasurementRange";

  low: Maybe<number>;

  high: Maybe<number>;

  units: Maybe<MeasurementUnits>;

  complexUnits: Maybe<MeasurementRangeComplexUnits>;
};

export type MeasurementRangeComplexUnits = {
  __typename?: "ComplexUnits";

  denominator: Maybe<MeasurementUnits[]>;

  numerator: Maybe<MeasurementUnits[]>;
};

export type MetricValueFragment = {
  __typename?: "RMetric";

  value: Maybe<number>;

  exactness: Maybe<Exactness>;
};

export type QualifierFragment = {
  __typename?: "Qualifier";

  name: string;

  relation: QualifierRelation;

  value: Maybe<QualifierValue>;

  negate: Maybe<boolean>;

  range: Maybe<Qualifier_Range>;

  freeText: Maybe<string>;

  source: Maybe<FindingSource>;
};

export type QualifierRelation = {
  __typename?: "Relation";

  name: string;

  id: string;

  type: Maybe<string>;

  multi: Maybe<boolean>;

  essence: Maybe<string>;
};

export type QualifierValue = {
  __typename?: "Feature";

  name: string;

  shortname: Maybe<string>;

  id: string;

  category: Maybe<QualifierCategory>;

  patientDescription: Maybe<string>;

  description: Maybe<QualifierDescription>;

  range: Maybe<QualifierRange>;

  parents: Maybe<QualifierParents>;
};

export type QualifierCategory = {
  __typename?: "Feature";

  id: string;

  name: string;

  shortname: Maybe<string>;
};

export type QualifierDescription = {
  __typename?: "Description";

  text: Maybe<string>;
};

export type QualifierRange = MeasurementRangeFragment;

export type QualifierParents = {
  __typename?: "FeatureSet";

  features: Maybe<QualifierFeatures[]>;
};

export type QualifierFeatures = {
  __typename?: "Feature";

  id: string;

  name: string;

  childrenSetType: Maybe<string>;
};

export type Qualifier_Range = MeasurementRangeFragment;

export type FeatureFragment = {
  __typename?: "Feature";

  name: string;

  id: string;

  shortname: Maybe<string>;

  patientDescription: Maybe<string>;

  alias: Maybe<string>;

  hasRefinement: Maybe<boolean>;

  organSystem: Maybe<FeatureOrganSystem>;

  category: Maybe<FeatureCategory>;

  qualifierSpec: Maybe<FeatureQualifierSpec[]>;

  description: Maybe<FeatureDescription>;

  range: Maybe<FeatureRange>;

  inferredParents: Maybe<FeatureInferredParents[]>;

  inferredChildren: Maybe<FeatureInferredChildren[]>;

  qualifiers: Maybe<FeatureQualifiers[]>;

  inferredQualifiers: Maybe<FeatureInferredQualifiers[]>;
};

export type FeatureOrganSystem = {
  __typename?: "Feature";

  id: string;

  name: string;

  shortname: Maybe<string>;
};

export type FeatureCategory = {
  __typename?: "Feature";

  id: string;

  name: string;

  shortname: Maybe<string>;
};

export type FeatureQualifierSpec = {
  __typename?: "QualifierSpec";

  relation: FeatureRelation;

  mandatory: Maybe<boolean>;
};

export type FeatureRelation = {
  __typename?: "Relation";

  name: string;

  id: string;

  type: Maybe<string>;
};

export type FeatureDescription = {
  __typename?: "Description";

  url: Maybe<string>;

  text: Maybe<string>;
};

export type FeatureRange = MeasurementRangeFragment;

export type FeatureInferredParents = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type FeatureInferredChildren = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type FeatureQualifiers = QualifierFragment;

export type FeatureInferredQualifiers = QualifierFragment;

export type QualifiedFeatureFragment = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  negate: Maybe<boolean>;

  skip: Maybe<boolean>;

  feature: Maybe<QualifiedFeatureFeature>;

  qualifiers: Maybe<QualifiedFeatureQualifiers[]>;

  mandatory: Maybe<QualifiedFeatureMandatory>;
};

export type QualifiedFeatureFeature = FeatureFragment;

export type QualifiedFeatureQualifiers = QualifierFragment;

export type QualifiedFeatureMandatory = {
  __typename?: "Qualifier";

  relation: QualifiedFeatureRelation;

  value: Maybe<QualifiedFeatureValue>;

  range: Maybe<QualifiedFeatureRange>;

  freeText: Maybe<string>;
};

export type QualifiedFeatureRelation = {
  __typename?: "Relation";

  name: string;

  id: string;

  type: Maybe<string>;
};

export type QualifiedFeatureValue = {
  __typename?: "Feature";

  name: string;

  id: string;
};

export type QualifiedFeatureRange = MeasurementRangeFragment;

export type GroupedFeatureResponseFragment = {
  __typename?: "GroupedFeatureResponse";

  feature: Maybe<GroupedFeatureResponseFeature>;

  skip: Maybe<boolean>;

  source: Maybe<FindingSource>;

  group: Maybe<CaseFindingPresentationType>;

  validation: Maybe<ValidationStatus>;
};

export type GroupedFeatureResponseFeature = QualifiedFeatureFragment;

export type CitationDataFragment = {
  __typename?: "CitationData";

  id: string;

  kahunId: Maybe<string>;

  vancouver: Maybe<string>;

  url: Maybe<string>;

  title: Maybe<string>;

  containerTitle: Maybe<string>;

  DOI: Maybe<string>;
};

export type CitationsFragment = {
  __typename?: "Citations";

  totalCount: Maybe<number>;

  citationsData: Maybe<CitationsCitationsData[]>;
};

export type CitationsCitationsData = CitationDataFragment;

export type SheetFragment = {
  __typename?: "Sheet";

  rows: Maybe<SheetRows[]>;
};

export type SheetRows = {
  __typename?: "Row";

  cols: Maybe<string[]>;
};

export type DebugSheetFragment = {
  __typename?: "DebugSheet";

  name: Maybe<string>;

  sheet: Maybe<DebugSheetSheet>;
};

export type DebugSheetSheet = SheetFragment;

export type FlagFragment = {
  __typename?: "Flag";

  type: Maybe<DdFlagType>;

  ref: Maybe<FlagRef>;
};

export type FlagRef = {
  __typename?: "QualifiedFeature";

  feature: Maybe<FlagFeature>;

  negate: Maybe<boolean>;
};

export type FlagFeature = {
  __typename?: "Feature";

  name: string;
};

export type NextStepFragment = {
  __typename?: "NextStep";

  suggestedFeatures: Maybe<NextStepSuggestedFeatures[]>;

  debugSheet: Maybe<NextStepDebugSheet>;
};

export type NextStepSuggestedFeatures = {
  __typename?: "SuggestedFeature";

  qualifiedFeature: Maybe<NextStepQualifiedFeature>;

  sumDiseaseGivenFinding: Maybe<number>;

  sumLiftScores: Maybe<number>;

  liftScores: Maybe<NextStepLiftScores[]>;

  category: Maybe<DiseaseCategoryType>;
};

export type NextStepQualifiedFeature = QualifiedFeatureFragment;

export type NextStepLiftScores = {
  __typename?: "LiftScore";

  liftScore: Maybe<number>;

  qualifiedFeature: Maybe<NextStep_QualifiedFeature>;
};

export type NextStep_QualifiedFeature = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type NextStepDebugSheet = SheetFragment;

export type PatientSuggestionsFragment = {
  __typename?: "PatientSuggestions";

  debugSheet: Maybe<PatientSuggestionsDebugSheet>;

  suggestions: Maybe<PatientSuggestionsSuggestions[]>;

  clustered: Maybe<PatientSuggestionsClustered[]>;
};

export type PatientSuggestionsDebugSheet = SheetFragment;

export type PatientSuggestionsSuggestions = {
  __typename?: "PatientSuggestion";

  suggestion: Maybe<PatientSuggestionsSuggestion>;

  entropy: Maybe<number>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;

  findingProbability: Maybe<number>;

  findingEntropy: Maybe<number>;

  findingLeakAdjustedEntropy: Maybe<number>;

  findingLeak: Maybe<number>;

  category: Maybe<DiseaseCategoryType>;

  inPatient: Maybe<boolean>;

  inPatientId: Maybe<string>;

  perDiseaseMetrics: Maybe<PatientSuggestionsPerDiseaseMetrics[]>;
};

export type PatientSuggestionsSuggestion = QualifiedFeatureFragment;

export type PatientSuggestionsPerDiseaseMetrics = {
  __typename?: "SuggestionDiseaseMetric";

  suggestion: Maybe<PatientSuggestions_Suggestion>;

  specificity: Maybe<number>;

  pairMetrics: Maybe<PatientSuggestionsPairMetrics>;

  diseaseIndex: Maybe<number>;

  findingGivenDisease: Maybe<number>;

  adjustedBinaryEntropy: Maybe<number>;
};

export type PatientSuggestions_Suggestion = {
  __typename?: "QualifiedFeature";

  qualifiers: Maybe<PatientSuggestionsQualifiers[]>;
};

export type PatientSuggestionsQualifiers = QualifierFragment;

export type PatientSuggestionsPairMetrics = {
  __typename?: "RPairMetrics";

  sensitivity: Maybe<PatientSuggestionsSensitivity>;
};

export type PatientSuggestionsSensitivity = MetricValueFragment;

export type PatientSuggestionsClustered = {
  __typename?: "ClusterSuggestion";

  title: Maybe<PatientSuggestionsTitle>;

  items: Maybe<PatientSuggestionsItems[]>;

  category: Maybe<DiseaseCategoryType>;
};

export type PatientSuggestionsTitle = FeatureFragment;

export type PatientSuggestionsItems = {
  __typename?: "PatientSuggestion";

  suggestion: Maybe<PatientSuggestions__Suggestion>;

  entropy: Maybe<number>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;

  perDiseaseMetrics: Maybe<PatientSuggestions_PerDiseaseMetrics[]>;
};

export type PatientSuggestions__Suggestion = QualifiedFeatureFragment;

export type PatientSuggestions_PerDiseaseMetrics = {
  __typename?: "SuggestionDiseaseMetric";

  suggestion: Maybe<PatientSuggestions___Suggestion>;

  diseaseIndex: Maybe<number>;

  findingGivenDisease: Maybe<number>;
};

export type PatientSuggestions___Suggestion = {
  __typename?: "QualifiedFeature";

  qualifiers: Maybe<PatientSuggestions_Qualifiers[]>;
};

export type PatientSuggestions_Qualifiers = QualifierFragment;

export type PatientSuggestionMiExplanationsFragment = {
  __typename?: "PatientSuggestion";

  perDiseaseMetrics: Maybe<PatientSuggestionMiExplanationsPerDiseaseMetrics[]>;

  bestSubtype: Maybe<PatientSuggestionMiExplanationsBestSubtype>;

  findingProbability: Maybe<number>;

  findingEntropy: Maybe<number>;

  findingLeak: Maybe<number>;

  findingLeakAdjustedEntropy: Maybe<number>;

  defaultFindingGivenDisease: Maybe<number>;

  defaultFindingGivenDiseaseAdjustedEntropy: Maybe<number>;
};

export type PatientSuggestionMiExplanationsPerDiseaseMetrics = {
  __typename?: "SuggestionDiseaseMetric";

  diseaseIndex: Maybe<number>;

  findingGivenDisease: Maybe<number>;

  adjustedBinaryEntropy: Maybe<number>;

  specificity: Maybe<number>;

  likelihoodRatioNeg: Maybe<number>;

  likelihoodRatioPos: Maybe<number>;

  pairMetrics: Maybe<PatientSuggestionMiExplanationsPairMetrics>;

  suggestion: Maybe<PatientSuggestionMiExplanationsSuggestion>;
};

export type PatientSuggestionMiExplanationsPairMetrics = {
  __typename?: "RPairMetrics";

  sensitivity: Maybe<PatientSuggestionMiExplanationsSensitivity>;

  ppv: Maybe<PatientSuggestionMiExplanationsPpv>;

  incidence: Maybe<PatientSuggestionMiExplanationsIncidence>;

  rrisk_cause: Maybe<PatientSuggestionMiExplanationsRriskCause>;

  rrisk_effect: Maybe<PatientSuggestionMiExplanationsRriskEffect>;
};

export type PatientSuggestionMiExplanationsSensitivity = MetricValueFragment;

export type PatientSuggestionMiExplanationsPpv = MetricValueFragment;

export type PatientSuggestionMiExplanationsIncidence = MetricValueFragment;

export type PatientSuggestionMiExplanationsRriskCause = MetricValueFragment;

export type PatientSuggestionMiExplanationsRriskEffect = MetricValueFragment;

export type PatientSuggestionMiExplanationsSuggestion = QualifiedFeatureFragment;

export type PatientSuggestionMiExplanationsBestSubtype = QualifiedFeatureFragment;

export type ConversationDdFragment = {
  __typename?: "DDResponse";

  allFindings: Maybe<ConversationDdAllFindings[]>;

  clinicalReasoning: Maybe<ConversationDdClinicalReasoning[]>;

  clustersToConsider: Maybe<ConversationDdClustersToConsider[]>;

  options: Maybe<DiagnosisOptionsInput>;
};

export type ConversationDdAllFindings = QualifiedFeatureFragment;

export type ConversationDdClinicalReasoning = {
  __typename?: "ClinicalReasoning";

  id: string;

  inInitialDisorders: Maybe<boolean>;

  forcedInSort: Maybe<SortType[]>;

  disease: Maybe<ConversationDdDisease[]>;

  diseaseCitations: Maybe<ConversationDdDiseaseCitations>;

  findings: Maybe<ConversationDdFindings[]>;

  score: Maybe<number>;

  scores: Maybe<ConversationDdScores[]>;

  urgency: Maybe<number>;

  diseaseGivenIndependentFindings: Maybe<number>;

  diseaseGivenDependentFindings: Maybe<number>;

  inTopCRPerSortType: Maybe<SortType[]>;

  missingPresentingSymptoms: Maybe<boolean>;

  presentableDisease: Maybe<boolean>;

  patientIncidence: Maybe<number>;

  flags: Maybe<ConversationDdFlags[]>;
};

export type ConversationDdDisease = {
  __typename?: "QualifiedFeature";

  urgencyInfo: Maybe<ConversationDdUrgencyInfo>;
} & QualifiedFeatureFragment;

export type ConversationDdUrgencyInfo = {
  __typename?: "Feature";

  id: string;

  urgency: Maybe<number>;

  urgencyId: Maybe<string>;
};

export type ConversationDdDiseaseCitations = CitationsFragment;

export type ConversationDdFindings = {
  __typename?: "ClinicalReasoningFinding";

  finding: Maybe<ConversationDdFinding>;

  sourceQFIndex: Maybe<number>;

  absolutePredictiveValue: Maybe<number>;

  likelihoodRatio: Maybe<number>;
};

export type ConversationDdFinding = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type ConversationDdScores = {
  __typename?: "ScoreByType";

  postClusterAdjustment: Maybe<number>;

  sortType: Maybe<SortType>;
};

export type ConversationDdFlags = FlagFragment;

export type ConversationDdClustersToConsider = {
  __typename?: "DiseaseCluster";

  id: string;

  crIds: Maybe<string[]>;

  sortType: Maybe<SortType>;

  scores: Maybe<ConversationDd_Scores>;

  title: Maybe<ConversationDdTitle>;

  bestCrId: Maybe<string>;

  flags: Maybe<DdFlagType[]>;

  urgencyId: Maybe<string>;
};

export type ConversationDd_Scores = {
  __typename?: "ScoreByType";

  adjustedNormalized: Maybe<number>;
};

export type ConversationDdTitle = {
  __typename?: "QualifiedFeature";

  urgencyInfo: Maybe<ConversationDd_UrgencyInfo>;
} & QualifiedFeatureFragment;

export type ConversationDd_UrgencyInfo = {
  __typename?: "Feature";

  id: string;

  urgency: Maybe<number>;

  urgencyId: Maybe<string>;
};

export type ConversationNextSuggestionsFragment = {
  __typename?: "PatientSuggestions";

  clusters: Maybe<ConversationNextSuggestionsClusters[]>;

  diseases: Maybe<ConversationNextSuggestionsDiseases[]>;

  suggestions: Maybe<ConversationNextSuggestionsSuggestions[]>;

  debugReport: Maybe<string[]>;
};

export type ConversationNextSuggestionsClusters = {
  __typename?: "DiseaseCluster";

  id: string;

  crIds: Maybe<string[]>;

  sortType: Maybe<SortType>;

  title: Maybe<ConversationNextSuggestionsTitle>;
};

export type ConversationNextSuggestionsTitle = QualifiedFeatureFragment;

export type ConversationNextSuggestionsDiseases = {
  __typename?: "DiseaseForPlan";

  disease: Maybe<ConversationNextSuggestionsDisease>;

  complicationOf: Maybe<ConversationNextSuggestionsComplicationOf>;

  complicationOfObj: Maybe<ConversationNextSuggestionsComplicationOfObj>;

  crId: Maybe<string>;

  prob: Maybe<number>;

  inTopCRPerSortType: Maybe<SortType[]>;

  selectedBySortType: Maybe<SortType[]>;

  patientIncidence: Maybe<number>;

  diseaseCitations: Maybe<ConversationNextSuggestionsDiseaseCitations>;

  crFindings: Maybe<ConversationNextSuggestionsCrFindings[]>;
};

export type ConversationNextSuggestionsDisease = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  feature: Maybe<ConversationNextSuggestionsFeature>;
};

export type ConversationNextSuggestionsFeature = {
  __typename?: "Feature";

  urgencyId: Maybe<string>;
};

export type ConversationNextSuggestionsComplicationOf = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;
};

export type ConversationNextSuggestionsComplicationOfObj = {
  __typename?: "EtiologiesOfComplication";

  etiologies: Maybe<ConversationNextSuggestionsEtiologies[]>;
};

export type ConversationNextSuggestionsEtiologies = {
  __typename?: "EtiologyOfComplication";

  disease: Maybe<ConversationNextSuggestions_Disease>;

  sensitivity: Maybe<number>;
};

export type ConversationNextSuggestions_Disease = {
  __typename?: "QualifiedFeature";

  name: string;

  id: string;
};

export type ConversationNextSuggestionsDiseaseCitations = CitationsFragment;

export type ConversationNextSuggestionsCrFindings = {
  __typename?: "ClinicalReasoningFinding";

  finding: Maybe<ConversationNextSuggestionsFinding>;

  gfinding: Maybe<ConversationNextSuggestionsGfinding>;

  score: Maybe<number>;

  absolutePredictiveValue: Maybe<number>;

  likelihoodRatio: Maybe<number>;
};

export type ConversationNextSuggestionsFinding = QualifiedFeatureFragment;

export type ConversationNextSuggestionsGfinding = GroupedFeatureResponseFragment;

export type ConversationNextSuggestionsSuggestions = {
  __typename?: "PatientSuggestion";

  suggestion: Maybe<ConversationNextSuggestionsSuggestion>;

  category: Maybe<DiseaseCategoryType>;

  entropy: Maybe<number>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;
} & PatientSuggestionMiExplanationsFragment;

export type ConversationNextSuggestionsSuggestion = QualifiedFeatureFragment;

export type GraphFragment = {
  __typename?: "Graph";

  nodes: Maybe<GraphNodes[]>;

  edges: Maybe<GraphEdges[]>;
};

export type GraphNodes = {
  __typename?: "Node";

  id: string;

  qfs: Maybe<GraphQfs[]>;

  probability: Maybe<number>;

  percentRange: Maybe<GraphPercentRange>;

  isConnected: Maybe<boolean>;

  type: Maybe<NodeType>;

  sourceQFIndex: Maybe<number>;

  sourceQF: Maybe<GraphSourceQf>;
};

export type GraphQfs = QualifiedFeatureFragment;

export type GraphPercentRange = {
  __typename?: "Range";

  min: Maybe<number>;

  max: Maybe<number>;
};

export type GraphSourceQf = QualifiedFeatureFragment;

export type GraphEdges = {
  __typename?: "Edge";

  id: string;

  sourceID: string;

  targetID: string;

  prob: Maybe<number>;

  lift: Maybe<number>;

  type: Maybe<RuleType>;

  usedInComputation: Maybe<boolean>;

  ruleValueId: Maybe<string>;
};

export type CategoryFragment = {
  __typename?: "Feature";

  name: string;

  id: string;

  shortname: Maybe<string>;
};

export type PanelFragment = {
  __typename?: "Panel";

  name: string;

  id: string;

  shortname: Maybe<string>;

  category: Maybe<PanelCategory>;

  panelGroup: Maybe<PanelPanelGroup>;

  panelSubgroup: Maybe<PanelPanelSubgroup>;

  findings: Maybe<PanelFindings[]>;
};

export type PanelCategory = CategoryFragment;

export type PanelPanelGroup = CategoryFragment;

export type PanelPanelSubgroup = CategoryFragment;

export type PanelFindings = QualifiedFeatureFragment;

export type PatientFirstSummaryFragment = {
  __typename?: "SummeryData";

  age: Maybe<number>;

  gender: Maybe<string>;

  main: Maybe<string>;

  visitReason: Maybe<PatientFirstSummaryVisitReason[]>;

  sections: Maybe<PatientFirstSummarySections[]>;
};

export type PatientFirstSummaryVisitReason = {
  __typename?: "SummaryVisitReason";

  id: Maybe<string>;

  name: Maybe<string>;
};

export type PatientFirstSummarySections = {
  __typename?: "SummerySection";

  content: Maybe<string>;

  title: Maybe<string>;

  type: Maybe<SummarySectionType>;
};

export type ConversationSuggestionFragment = {
  __typename?: "PatientSuggestion";

  questionType: Maybe<ConversationQuestionType>;

  suggestion: Maybe<ConversationSuggestionSuggestion>;

  refinements: Maybe<ConversationSuggestionRefinements[]>;

  entropy: Maybe<number>;

  entropyYes: Maybe<number>;

  entropyNo: Maybe<number>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;

  expectation: Maybe<number>;
} & PatientSuggestionMiExplanationsFragment;

export type ConversationSuggestionSuggestion = QualifiedFeatureFragment;

export type ConversationSuggestionRefinements = QualifiedFeatureFragment;

export type ShareCodeDataFragmentFragment = {
  __typename?: "ShareCodeData";

  id: Maybe<string>;

  date: Maybe<number>;

  shareid: Maybe<string>;

  seen: Maybe<string[]>;

  digitCode: Maybe<string>;

  comment: Maybe<string>;
};

export type VisitStatusFragmentFragment = {
  __typename?: "VisitStatusStructure";

  age: Maybe<number>;

  gender: Maybe<string>;

  status: Maybe<string>;

  visitReason: Maybe<VisitStatusFragmentVisitReason[]>;

  chiefComplaint: Maybe<VisitStatusFragmentChiefComplaint>;
};

export type VisitStatusFragmentVisitReason = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type VisitStatusFragmentChiefComplaint = {
  __typename?: "Feature";

  id: string;

  name: string;

  shortname: Maybe<string>;
};

export type CreateCodeDataFragmentFragment = {
  __typename?: "CreatedCode";

  id: Maybe<string>;

  ehrid: Maybe<string>;

  shareid: Maybe<string>;

  digitCode: Maybe<string>;
};

export type ConversationRecommendationFragment = {
  __typename?: "PolicyRecommendation";

  feature: Maybe<ConversationRecommendationFeature>;

  policyName: Maybe<string>;

  policyid: Maybe<string>;

  protocolid: Maybe<string>;

  protocolName: Maybe<string>;
};

export type ConversationRecommendationFeature = QualifiedFeatureFragment;

export type ConversationInfoMessageFragment = {
  __typename?: "ConversationInfoMessage";

  id: Maybe<string>;

  infoText: Maybe<string>;

  type: Maybe<string>;

  confirm: Maybe<boolean>;

  confirmText: Maybe<string>;
};

export type ConversationExchangeResultFragment = {
  __typename?: "ConversationExchange";

  question: Maybe<ConversationExchangeResultQuestion>;

  answer: Maybe<ConversationExchangeResultAnswer[]>;

  state: Maybe<ConversationExchangeResultState>;
};

export type ConversationExchangeResultQuestion = {
  __typename?: "ConversationQuestion";

  questionType: Maybe<ConversationQuestionType>;

  suggestions: Maybe<ConversationExchangeResultSuggestions[]>;

  basedOnReasoning: Maybe<boolean>;
};

export type ConversationExchangeResultSuggestions = ConversationSuggestionFragment;

export type ConversationExchangeResultAnswer = {
  __typename?: "ConversationExchangeAnswer";

  suggestionId: Maybe<string>;

  refinementId: Maybe<string>;

  patientAnswer: Maybe<ConversationPatientAnswer>;

  finding: Maybe<ConversationExchangeResultFinding>;
};

export type ConversationExchangeResultFinding = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  negate: Maybe<boolean>;

  skip: Maybe<boolean>;

  feature: Maybe<ConversationExchangeResultFeature>;

  qualifiers: Maybe<ConversationExchangeResultQualifiers[]>;

  mandatory: Maybe<ConversationExchangeResultMandatory>;
};

export type ConversationExchangeResultFeature = FeatureFragment;

export type ConversationExchangeResultQualifiers = {
  __typename?: "Qualifier";

  source: Maybe<FindingSource>;

  name: string;

  relation: ConversationExchangeResultRelation;

  value: Maybe<ConversationExchangeResultValue>;

  negate: Maybe<boolean>;

  range: Maybe<ConversationExchangeResult_Range>;

  freeText: Maybe<string>;
};

export type ConversationExchangeResultRelation = {
  __typename?: "Relation";

  name: string;

  id: string;

  type: Maybe<string>;

  multi: Maybe<boolean>;
};

export type ConversationExchangeResultValue = {
  __typename?: "Feature";

  name: string;

  shortname: Maybe<string>;

  id: string;

  category: Maybe<ConversationExchangeResultCategory>;

  range: Maybe<ConversationExchangeResultRange>;

  parents: Maybe<ConversationExchangeResultParents>;

  patientDescription: Maybe<string>;

  description: Maybe<ConversationExchangeResultDescription>;
};

export type ConversationExchangeResultCategory = {
  __typename?: "Feature";

  id: string;

  name: string;

  shortname: Maybe<string>;
};

export type ConversationExchangeResultRange = MeasurementRangeFragment;

export type ConversationExchangeResultParents = {
  __typename?: "FeatureSet";

  features: Maybe<ConversationExchangeResultFeatures[]>;
};

export type ConversationExchangeResultFeatures = {
  __typename?: "Feature";

  id: string;

  name: string;

  childrenSetType: Maybe<string>;
};

export type ConversationExchangeResultDescription = {
  __typename?: "Description";

  text: Maybe<string>;
};

export type ConversationExchangeResult_Range = MeasurementRangeFragment;

export type ConversationExchangeResultMandatory = {
  __typename?: "Qualifier";

  relation: ConversationExchangeResult_Relation;

  value: Maybe<ConversationExchangeResult_Value>;

  range: Maybe<ConversationExchangeResult__Range>;

  freeText: Maybe<string>;
};

export type ConversationExchangeResult_Relation = {
  __typename?: "Relation";

  name: string;

  id: string;

  type: Maybe<string>;
};

export type ConversationExchangeResult_Value = {
  __typename?: "Feature";

  name: string;

  id: string;
};

export type ConversationExchangeResult__Range = MeasurementRangeFragment;

export type ConversationExchangeResultState = {
  __typename?: "ConversationState";

  context: Maybe<ConversationContext>;

  entropy: Maybe<number>;

  mutualInformation: Maybe<number>;

  caseId: Maybe<string>;

  nextSuggestions: Maybe<ConversationExchangeResultNextSuggestions>;

  diagnosis: Maybe<ConversationExchangeResultDiagnosis[]>;
};

export type ConversationExchangeResultNextSuggestions = {
  __typename?: "PatientSuggestions";

  diseases: Maybe<ConversationExchangeResultDiseases[]>;

  suggestions: Maybe<ConversationExchangeResult_Suggestions[]>;
};

export type ConversationExchangeResultDiseases = {
  __typename?: "DiseaseForPlan";

  disease: Maybe<ConversationExchangeResultDisease>;

  prob: Maybe<number>;
};

export type ConversationExchangeResultDisease = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;
};

export type ConversationExchangeResult_Suggestions = {
  __typename?: "PatientSuggestion";

  perDiseaseMetrics: Maybe<ConversationExchangeResultPerDiseaseMetrics[]>;

  suggestion: Maybe<ConversationExchangeResult_Suggestion>;

  findingEntropy: Maybe<number>;

  findingLeak: Maybe<number>;

  findingProbability: Maybe<number>;

  category: Maybe<DiseaseCategoryType>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;
};

export type ConversationExchangeResultPerDiseaseMetrics = {
  __typename?: "SuggestionDiseaseMetric";

  suggestion: Maybe<ConversationExchangeResultSuggestion>;

  disease: Maybe<ConversationExchangeResult_Disease>;

  findingGivenDisease: Maybe<number>;
};

export type ConversationExchangeResultSuggestion = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type ConversationExchangeResult_Disease = {
  __typename?: "QualifiedFeature";

  id: string;
};

export type ConversationExchangeResult_Suggestion = {
  __typename?: "QualifiedFeature";

  id: string;

  name: string;

  qualifiers: Maybe<ConversationExchangeResult_Qualifiers[]>;
};

export type ConversationExchangeResult_Qualifiers = {
  __typename?: "Qualifier";

  name: string;

  relation: ConversationExchangeResult__Relation;

  range: Maybe<ConversationExchangeResult___Range>;
};

export type ConversationExchangeResult__Relation = {
  __typename?: "Relation";

  id: string;
};

export type ConversationExchangeResult___Range = {
  __typename?: "MeasurementRange";

  low: Maybe<number>;

  high: Maybe<number>;

  units: Maybe<MeasurementUnits>;
};

export type ConversationExchangeResultDiagnosis = {
  __typename?: "ClinicalReasoning";

  diseaseGivenDependentFindings: Maybe<number>;

  urgency: Maybe<number>;

  disease: Maybe<ConversationExchangeResult__Disease[]>;
};

export type ConversationExchangeResult__Disease = {
  __typename?: "QualifiedFeature";

  name: string;

  feature: Maybe<ConversationExchangeResult_Feature>;
};

export type ConversationExchangeResult_Feature = {
  __typename?: "Feature";

  id: string;

  parents: Maybe<ConversationExchangeResult_Parents>;
};

export type ConversationExchangeResult_Parents = {
  __typename?: "FeatureSet";

  features: Maybe<ConversationExchangeResult_Features[]>;
};

export type ConversationExchangeResult_Features = {
  __typename?: "Feature";

  id: string;
};

export type ConversationNextQuestionFragment = {
  __typename?: "ConversationQuestion";

  id: Maybe<string>;

  questionType: Maybe<ConversationQuestionType>;

  questionChoiceType: Maybe<QuestionChoiceType>;

  description: Maybe<string>;

  debugInfo: Maybe<StructuredDebugInfo>;

  elapsed: Maybe<number>;

  question: Maybe<ConversationNextQuestionQuestion>;

  suggestions: Maybe<ConversationNextQuestionSuggestions[]>;

  basedOnReasoning: Maybe<boolean>;
};

export type ConversationNextQuestionQuestion = {
  __typename?: "PatientQuestion";

  text: Maybe<string>;
};

export type ConversationNextQuestionSuggestions = {
  __typename?: "PatientSuggestion";

  questionType: Maybe<ConversationQuestionType>;

  canBeUnknown: Maybe<boolean>;

  refinementQuestionType: Maybe<QuestionChoiceType>;

  refinementLocationRange: Maybe<
    ConversationNextQuestionRefinementLocationRange
  >;

  question: Maybe<ConversationNextQuestion_Question>;

  suggestion: Maybe<ConversationNextQuestionSuggestion>;

  refinements: Maybe<ConversationNextQuestionRefinements[]>;

  mutualInformation: Maybe<number>;

  mutualInformationUrgencyDifferentiation: Maybe<number>;
} & PatientSuggestionMiExplanationsFragment;

export type ConversationNextQuestionRefinementLocationRange = {
  __typename?: "Feature";

  id: string;

  name: string;
};

export type ConversationNextQuestion_Question = {
  __typename?: "PatientQuestion";

  text: Maybe<string>;
};

export type ConversationNextQuestionSuggestion = QualifiedFeatureFragment;

export type ConversationNextQuestionRefinements = QualifiedFeatureFragment;

export type ConversationExchangeFragment = {
  __typename?: "ConversationExchange";

  question: Maybe<ConversationExchangeQuestion>;

  answer: Maybe<ConversationExchangeAnswer[]>;

  messages: Maybe<ConversationExchangeMessages[]>;

  patientAnswer: Maybe<ConversationPatientAnswer>;

  progress: Maybe<number>;
};

export type ConversationExchangeQuestion = ConversationNextQuestionFragment;

export type ConversationExchangeAnswer = {
  __typename?: "ConversationExchangeAnswer";

  suggestionId: Maybe<string>;

  refinementId: Maybe<string>;

  patientAnswer: Maybe<ConversationPatientAnswer>;

  finding: Maybe<ConversationExchangeFinding>;

  findingAlias: Maybe<string>;
};

export type ConversationExchangeFinding = QualifiedFeatureFragment;

export type ConversationExchangeMessages = ConversationInfoMessageFragment;

export type ConversationStateFragment = {
  __typename?: "ConversationState";

  gender: Maybe<string>;

  progress: Maybe<number>;

  entropy: Maybe<number>;

  context: Maybe<ConversationContext>;

  caseId: Maybe<string>;

  caseReadOnly: Maybe<boolean>;

  caseBasedOn: Maybe<string>;

  initialMessages: Maybe<ConversationStateInitialMessages[]>;

  urgent: Maybe<ConversationStateUrgent>;

  nextSuggestions: Maybe<ConversationStateNextSuggestions>;

  ddClusters: Maybe<ConversationStateDdClusters>;

  recommendations: Maybe<ConversationStateRecommendations[]>;

  navigationRecommendation: Maybe<ConversationStateNavigationRecommendation>;

  patientFindings: Maybe<ConversationStatePatientFindings[]>;

  patientIcd: Maybe<ConversationStatePatientIcd>;

  options: Maybe<ConversationOptionsInput>;
};

export type ConversationStateInitialMessages = ConversationInfoMessageFragment;

export type ConversationStateUrgent = {
  __typename?: "DDUrgency";

  urgencyLevelId: Maybe<string>;
};

export type ConversationStateNextSuggestions = ConversationNextSuggestionsFragment;

export type ConversationStateDdClusters = {
  __typename?: "DDResponse";

  debugReport: Maybe<string[]>;
} & ConversationDdFragment;

export type ConversationStateRecommendations = ConversationRecommendationFragment;

export type ConversationStateNavigationRecommendation = ConversationRecommendationFragment;

export type ConversationStatePatientFindings = GroupedFeatureResponseFragment;

export type ConversationStatePatientIcd = {
  __typename?: "StateICDCodes";

  diseases: Maybe<ConversationStateDiseases[]>;

  findings: Maybe<ConversationStateFindings[]>;
};

export type ConversationStateDiseases = {
  __typename?: "ICDCode";

  code: Maybe<string>;

  desc: Maybe<string>;

  dueTo: Maybe<ConversationStateDueTo[]>;
};

export type ConversationStateDueTo = QualifiedFeatureFragment;

export type ConversationStateFindings = {
  __typename?: "ICDCode";

  code: Maybe<string>;

  desc: Maybe<string>;

  dueTo: Maybe<ConversationState_DueTo[]>;
};

export type ConversationState_DueTo = QualifiedFeatureFragment;

export type ConversationStateNextQFragment = {
  __typename?: "ConversationState";

  nextQuestion: Maybe<ConversationStateNextQNextQuestion>;
} & ConversationStateFragment;

export type ConversationStateNextQNextQuestion = ConversationNextQuestionFragment;

export type ConversationStateNextResponseFragment = {
  __typename?: "ConversationState";

  nextResponse: Maybe<ConversationStateNextResponseNextResponse>;
} & ConversationStateFragment;

export type ConversationStateNextResponseNextResponse = {
  __typename?: "ConversationResponse";

  question: Maybe<ConversationStateNextResponseQuestion>;

  messages: Maybe<ConversationStateNextResponseMessages[]>;
};

export type ConversationStateNextResponseQuestion = ConversationNextQuestionFragment;

export type ConversationStateNextResponseMessages = ConversationInfoMessageFragment;

export type GeneratedOutputFragment = {
  __typename?: "GeneratedOutput";

  generating: Maybe<boolean>;

  error: Maybe<boolean>;

  errorText: Maybe<string>;

  etag: Maybe<string>;
};

export type SummaryTextFragment = {
  __typename?: "SummaryText";

  text: Maybe<string>;
} & GeneratedOutputFragment;

export type GroundedSummaryTextFragment = {
  __typename?: "GroundedSummaryText";

  grounding: Maybe<GroundedSummaryTextGrounding>;
} & SummaryTextFragment;

export type GroundedSummaryTextGrounding = {
  __typename?: "GeneratedGrounding";

  grounding: Maybe<GroundedSummaryText_Grounding>;
} & GeneratedOutputFragment;

export type GroundedSummaryText_Grounding = GroundingFragment;

export type LoadedModelInfoFragment = {
  __typename?: "LoadedModelInfo";

  commitSerial: Maybe<number>;

  revision: Maybe<string>;
};

export type BackendModelInfoFragment = {
  __typename?: "Query";

  gatewayModel: Maybe<BackendModelInfoGatewayModel>;

  diagnosisModel: Maybe<BackendModelInfoDiagnosisModel>;
};

export type BackendModelInfoGatewayModel = LoadedModelInfoFragment;

export type BackendModelInfoDiagnosisModel = LoadedModelInfoFragment;

export type SummaryDiseaseClusterFragment = {
  __typename?: "SummaryDiseaseCluster";

  title: Maybe<SummaryDiseaseClusterTitle>;

  inTopCRPerSortType: Maybe<SortType[]>;

  scores: Maybe<SummaryDiseaseClusterScores>;

  icd: Maybe<SummaryDiseaseClusterIcd[]>;

  explanation: Maybe<SummaryDiseaseClusterExplanation>;
};

export type SummaryDiseaseClusterTitle = QualifiedFeatureFragment;

export type SummaryDiseaseClusterScores = {
  __typename?: "ScoreByType";

  sortType: Maybe<SortType>;

  adjustedNormalized: Maybe<number>;

  postClusterAdjustment: Maybe<number>;
};

export type SummaryDiseaseClusterIcd = {
  __typename?: "SummaryICDCode";

  code: Maybe<string>;

  desc: Maybe<string>;
};

export type SummaryDiseaseClusterExplanation = GroundedSummaryTextFragment;

export type SummaryProblemFragment = {
  __typename?: "SummaryProblem";

  problem: Maybe<SummaryProblemProblem>;

  icd: Maybe<SummaryProblemIcd[]>;
};

export type SummaryProblemProblem = QualifiedFeatureFragment;

export type SummaryProblemIcd = {
  __typename?: "SummaryICDCode";

  code: Maybe<string>;

  desc: Maybe<string>;
};

export type RuleValueFragment = {
  __typename?: "RuleValue";

  ruleValueId: Maybe<string>;

  prob: Maybe<number>;

  lift: Maybe<number>;

  type: Maybe<RuleType>;

  citationsObj: Maybe<RuleValueCitationsObj>;

  citations: Maybe<string[]>;

  exactCitation: Maybe<boolean>;
};

export type RuleValueCitationsObj = {
  __typename?: "CitationData";

  id: string;

  vancouver: Maybe<string>;

  url: Maybe<string>;

  title: Maybe<string>;

  containerTitle: Maybe<string>;

  DOI: Maybe<string>;
};

export type RuleStatFragment = {
  __typename?: "RuleStat";

  overloadSource: Maybe<RuleStatOverloadSource[]>;

  overloadTarget: Maybe<RuleStatOverloadTarget[]>;

  overloadSourceType: Maybe<RuleType>;

  overloadTargetType: Maybe<RuleType>;

  prob: Maybe<number>;

  lift: Maybe<number>;

  type: Maybe<RuleType>;

  ruleValue: Maybe<RuleStatRuleValue>;

  edgeStat: Maybe<RuleStatEdgeStat>;
};

export type RuleStatOverloadSource = QualifiedFeatureFragment;

export type RuleStatOverloadTarget = QualifiedFeatureFragment;

export type RuleStatRuleValue = RuleValueFragment;

export type RuleStatEdgeStat = {
  __typename?: "MetricsEdgeStat";

  rate: Maybe<number>;

  unit: Maybe<MetricsRuleUnits>;
};

export type PairGraphFragment = {
  __typename?: "Graph";

  nodes: Maybe<PairGraphNodes[]>;

  edges: Maybe<PairGraphEdges[]>;
};

export type PairGraphNodes = {
  __typename?: "Node";

  id: string;

  qfs: Maybe<PairGraphQfs[]>;

  probability: Maybe<number>;

  percentRange: Maybe<PairGraphPercentRange>;

  isConnected: Maybe<boolean>;

  type: Maybe<NodeType>;

  sourceQFIndex: Maybe<number>;
};

export type PairGraphQfs = QualifiedFeatureFragment;

export type PairGraphPercentRange = {
  __typename?: "Range";

  min: Maybe<number>;

  max: Maybe<number>;
};

export type PairGraphEdges = {
  __typename?: "Edge";

  id: string;

  sourceID: string;

  targetID: string;

  prob: Maybe<number>;

  lift: Maybe<number>;

  type: Maybe<RuleType>;

  ruleValues: Maybe<PairGraphRuleValues[]>;

  usedInComputation: Maybe<boolean>;

  ruleValueId: Maybe<string>;
};

export type PairGraphRuleValues = RuleStatFragment;

export type GroundingFragment = {
  __typename?: "Grounding";

  sentences: Maybe<GroundingSentences[]>;

  debugData: Maybe<StructuredDebugInfo>;
};

export type GroundingSentences = {
  __typename?: "GroundedSentence";

  sentence: Maybe<string>;

  statements: Maybe<GroundingStatements[]>;
};

export type GroundingStatements = {
  __typename?: "GroundedStatement";

  id: string;

  runningIndex: Maybe<number>;

  statement: Maybe<Statement>;

  wordIndexForReferencePoint: Maybe<number>;

  wordsIndices: Maybe<number[]>;

  elementA: Maybe<GroundingElementA>;

  hypothesis: Maybe<StatementHypothesis>;

  elementB: Maybe<GroundingElementB>;

  connectionStrength: Maybe<ConnectionStrength>;

  edgesWithTextualReferences: Maybe<GroundingEdgesWithTextualReferences[]>;
};

export type GroundingElementA = {
  __typename?: "NodeValidation";

  element: Maybe<string>;

  match: Maybe<NodeMatch>;

  diff: Maybe<string>;
};

export type GroundingElementB = {
  __typename?: "NodeValidation";

  element: Maybe<string>;

  match: Maybe<NodeMatch>;

  diff: Maybe<string>;
};

export type GroundingEdgesWithTextualReferences = {
  __typename?: "EdgeWithTextualReferences";

  priority: Maybe<number>;

  rvWithTextualReference: Maybe<GroundingRvWithTextualReference[]>;
};

export type GroundingRvWithTextualReference = {
  __typename?: "TextualWithRuleReference";

  priority: Maybe<number>;

  textual: Maybe<string>;

  ruleValue: Maybe<GroundingRuleValue>;
};

export type GroundingRuleValue = {
  __typename?: "RuleValue";

  citationsObj: Maybe<GroundingCitationsObj>;
};

export type GroundingCitationsObj = CitationDataFragment;

export type FindingMetricsFragment = {
  __typename?: "DiseaseFindingMetrics";

  id: string;

  direction: Maybe<DiseaseFindingDirection>;

  category: Maybe<DiseaseCategoryType>;

  presentingSymptom: Maybe<boolean>;

  finding: Maybe<FindingMetricsFinding>;

  subcohort: Maybe<FindingMetricsSubcohort>;

  metrics: Maybe<FindingMetricsMetrics>;
};

export type FindingMetricsFinding = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<FindingMetricsQfs[]>;
};

export type FindingMetricsQfs = QualifiedFeatureFragment;

export type FindingMetricsSubcohort = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<FindingMetrics_Qfs[]>;
};

export type FindingMetrics_Qfs = QualifiedFeatureFragment;

export type FindingMetricsMetrics = {
  __typename?: "RPairMetrics";

  sensitivity: Maybe<FindingMetricsSensitivity>;

  specificity: Maybe<FindingMetricsSpecificity>;

  ppv: Maybe<FindingMetricsPpv>;

  rrisk_cause: Maybe<FindingMetricsRriskCause>;

  rrisk_effect: Maybe<FindingMetricsRriskEffect>;

  incidence: Maybe<FindingMetricsIncidence>;

  NLR: Maybe<FindingMetricsNlr>;

  PLR: Maybe<FindingMetricsPlr>;
};

export type FindingMetricsSensitivity = MetricValueFragment;

export type FindingMetricsSpecificity = MetricValueFragment;

export type FindingMetricsPpv = MetricValueFragment;

export type FindingMetricsRriskCause = MetricValueFragment;

export type FindingMetricsRriskEffect = MetricValueFragment;

export type FindingMetricsIncidence = MetricValueFragment;

export type FindingMetricsNlr = MetricValueFragment;

export type FindingMetricsPlr = MetricValueFragment;

export type DiseaseAgeDistributionFragment = {
  __typename?: "RAgeDistribution";

  ranges: Maybe<DiseaseAgeDistributionRanges[]>;
};

export type DiseaseAgeDistributionRanges = {
  __typename?: "RAgeDistributionRange";

  from: Maybe<number>;

  to: Maybe<number>;

  value: Maybe<number>;
};

export type UnaryMetricsFragment = {
  __typename?: "RUnaryMetrics";

  incidence: Maybe<number>;

  prevalence: Maybe<number>;

  prevalenceDescription: Maybe<string>;

  duration: Maybe<UnaryMetricsDuration>;

  urgency: Maybe<number>;
};

export type UnaryMetricsDuration = {
  __typename?: "MeasurementRange";

  low: Maybe<number>;

  high: Maybe<number>;

  units: Maybe<MeasurementUnits>;
};

export type DiseaseReferenceDataFragment = {
  __typename?: "DiseaseReference";

  id: string;

  disease: Maybe<DiseaseReferenceDataDisease[]>;

  findings: Maybe<DiseaseReferenceDataFindings[]>;

  citations: Maybe<DiseaseReferenceDataCitations>;

  citationsDVData: Maybe<DiseaseReferenceDataCitationsDvData[]>;

  metrics: Maybe<DiseaseReferenceDataMetrics>;
};

export type DiseaseReferenceDataDisease = QualifiedFeatureFragment;

export type DiseaseReferenceDataFindings = FindingMetricsFragment;

export type DiseaseReferenceDataCitations = CitationsFragment;

export type DiseaseReferenceDataCitationsDvData = {
  __typename?: "CitationDiseaseData";

  citationId: string;

  count: Maybe<number>;

  type: Maybe<CitationType>;

  findings: Maybe<DiseaseReferenceData_Findings[]>;

  subtypes: Maybe<DiseaseReferenceDataSubtypes[]>;
};

export type DiseaseReferenceData_Findings = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<DiseaseReferenceDataQfs[]>;
};

export type DiseaseReferenceDataQfs = QualifiedFeatureFragment;

export type DiseaseReferenceDataSubtypes = {
  __typename?: "RCohort";

  id: string;

  qfs: Maybe<DiseaseReferenceData_Qfs[]>;
};

export type DiseaseReferenceData_Qfs = QualifiedFeatureFragment;

export type DiseaseReferenceDataMetrics = UnaryMetricsFragment;

export type DiseaseReferenceOverviewDataFragment = {
  __typename?: "DiseaseReference";

  id: string;

  disease: Maybe<DiseaseReferenceOverviewDataDisease[]>;

  metrics: Maybe<DiseaseReferenceOverviewDataMetrics>;

  ageDistribution: Maybe<DiseaseReferenceOverviewDataAgeDistribution>;
};

export type DiseaseReferenceOverviewDataDisease = {
  __typename?: "QualifiedFeature";

  feature: Maybe<DiseaseReferenceOverviewDataFeature>;
} & QualifiedFeatureFragment;

export type DiseaseReferenceOverviewDataFeature = {
  __typename?: "Feature";

  parents: Maybe<DiseaseReferenceOverviewDataParents>;

  children: Maybe<DiseaseReferenceOverviewDataChildren>;

  subGroups: Maybe<DiseaseReferenceOverviewDataSubGroups[]>;

  superGroups: Maybe<DiseaseReferenceOverviewDataSuperGroups[]>;
};

export type DiseaseReferenceOverviewDataParents = {
  __typename?: "FeatureSet";

  features: Maybe<DiseaseReferenceOverviewDataFeatures[]>;
};

export type DiseaseReferenceOverviewDataFeatures = {
  __typename?: "Feature";

  id: string;
};

export type DiseaseReferenceOverviewDataChildren = {
  __typename?: "FeatureSet";

  features: Maybe<DiseaseReferenceOverviewData_Features[]>;
};

export type DiseaseReferenceOverviewData_Features = {
  __typename?: "Feature";

  id: string;
};

export type DiseaseReferenceOverviewDataSubGroups = {
  __typename?: "GroupRelation";

  feature: Maybe<DiseaseReferenceOverviewData_Feature>;

  type: Maybe<SubGroupType>;
};

export type DiseaseReferenceOverviewData_Feature = FeatureFragment;

export type DiseaseReferenceOverviewDataSuperGroups = {
  __typename?: "GroupRelation";

  feature: Maybe<DiseaseReferenceOverviewData__Feature>;

  type: Maybe<SubGroupType>;
};

export type DiseaseReferenceOverviewData__Feature = FeatureFragment;

export type DiseaseReferenceOverviewDataMetrics = UnaryMetricsFragment;

export type DiseaseReferenceOverviewDataAgeDistribution = DiseaseAgeDistributionFragment;
